/**
 * Interact with the view.
 *
 * @module core/view
 */

import './helpers';
import imagesLoaded from 'imagesloaded';
import * as App from './app';
import * as Analytics from './analytics';
import * as Form from './form';
import * as Filter from './filter';
import * as Util from './util';
import * as Matrix from './matrix';

/**
 * The primary view container.
 *
 * This jQuery object represents the current "working" area of the app, and is used throughout as a target for event listeners, data attributes, and anything else that needs to be tied to a reliable location. It is set and modified with [`setContainer`]{@link module:core/view~setContainer}.
 *
 * @since 3.0.0
 *
 * @type {jQuery}
 *
 * @see [`setContainer`]{@link module:core/view~setContainer}
 */
export let container;

/**
 * Store the current rendering state, allowing [`updateRenderState`]{@link module:core/view~updateRenderState} to check/update the current rendering state.
 *
 * @since 3.0.0
 *
 * @type {object}
 *
 * @see [`updateRenderState`]{@link module:core/view~updateRenderState}
 *
 * @example
 * {
 *   imagesLoading: true,
 *   rendering: false,
 * }
 */
export const renderState = {};

/**
 * Reference object to simplify keycodes for keyboard interactions. Included keycodes:
 *
 * `enter` `backspace` `tab` `down` `left` `right` `space` `up` `esc` `home` `end` `d` `p`
 *
 * @since 3.0.0
 *
 * @type {object}
 */
export const keycode = {
  enter: 13,
  backspace: 8,
  tab: 9,
  down: 40,
  left: 37,
  right: 39,
  space: 32,
  up: 38,
  esc: 27,
  home: 36,
  end: 35,
  pageUp: 33,
  pageDown: 34,
  d: 68,
  p: 80,
};

/**
 * Reference object to simplify utilizing the current app height and width. It contains a `width` and `height` value, as well as a `set()` method which is run during view [`init`]{@link module:core/view.init} and [`resize`]{@link module:core/view~resize}.
 *
 * Note that `width` is set using the current HTML width value, and `height` is set using the window height.
 *
 * @since 3.0.0
 *
 * @return {object}
 *
 * @example
 * dimensions.set();
 *
 * console.log(dimensions.width, dimensions.height);
 *
 * // expected output:
 * 1920, 1080
 */
export const dimensions = (() => {
  let width = 0;
  let height = 0;

  return {
    width,
    height,
    set: () => {
      dimensions.width = parseFloat($('html').css('width'));
      dimensions.height = parseFloat($(window).height());
    },
  };
})();

/**
 * Config for settings related to View module. Includes [Form config]{@link module:core/form.config}.
 *
 * Not all items included in view config can be configured externally. See [View content settings]{@link module:core/config.view} for configuration details.
 *
 * @since 3.2.4
 *
 * @type {object}
 */
export const config = {
  activeOptions: [],
  attract_mode: {
    enabled: false,
    delay: 10,
    on_length: 20,
    off_length: 5,
  },
  debounce: {},
  failedImages: {},
  form: Form.config,
  modal_product: false,
  QRCode: {
    enabled: false,
    options: {},
  },
  reload_products_on_state_change: false,
  show_more_products: {
    enabled: false,
    increment: 3,
    limit: -1,
  },
};

/**
 * Initialize page settings and prepare HTML for rendering.
 *
 * 1. App [container target is set]{@link module:core/view~setContainer}.
 * 1. [Update render state]{@link module:core/view~updateRenderState} to set `rendering` to `true`.
 * 1. [Dimensions are gathered]{@link module:core/view.dimensions} for current window.
 * 1. Required app [HTML elements are rendered]{@link module:core/view~renderAppElements}.
 * 1. [Hatch button script is initialized]{@link module:core/view~hatchScriptInit}, if necessary.
 * 1. [Event listeners are added]{@link module:core/view~addEventListeners} to view.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @see [`setContainer`]{@link module:core/view~setContainer}
 * @see [`updateRenderState`]{@link module:core/view~updateRenderState}
 * @see [`dimensions.set`]{@link module:core/view.dimensions}
 * @see [`hatchScriptInit`]{@link module:core/view~hatchScriptInit}
 * @see [`renderAppElements`]{@link module:core/view~renderAppElements}
 * @see [`addEventListeners`]{@link module:core/view~addEventListeners}
 * @see Used by:
 * @see [**`App.init`**]{@link module:core/app.init}
 */
export const init = () => {
  // set container
  setContainer();
  // update render state to start loader
  updateRenderState('rendering', true);
  // view setup
  dimensions.set();
  // render necessary app elements
  renderAppElements();
  // add hatch button script
  hatchScriptInit();
  // view event listeners
  addEventListeners();
}

/**
 * Manage navigation around the app, depending on the provided `nav` type or direction. It is also the method used to "start" the app view, during [`App.init`]{@link module:core/app.init}.
 *
 * - Current `nav` type as set on [`container`]{@link module:core/view.container} as attribute named `data-nav`.
 * - [Navigation analytics]{@link module:core/analytics.navigation} are fired.
 * - Pre-nav [project customization]{@link module:core/app.customize} is run.
 * - Provided `nav` values are run through a switch statement which calls any required functionality for the navigation type (see `nav` param for more info).
 * - Post-nav [project customization]{@link module:core/app.customize} is run.
 * - Finally, the necessary nav action is run. Typically, this is [`renderView`]{@link module:core/view~renderView} to show the next view, but in the case of modal side journeys, [`renderModal`]{@link module:core/view~renderModal} may be run.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {string} [nav] - Navigation type, or "direction" to navigate. Default: `start`
 *
 * Valid values include:
 * - `start` - [Start]{@link module:core/app.start} app, at either the `settings.start` or "dev" start, if dev build
 * - `restart` - [Start]{@link module:core/app.start} app at `current_start` value. Runs "restart" analytics
 * - `back` - [Backwards]{@link module:core/app.backward} to previous view
 * - `view-all` - [Initialize "view all" content]{@link module:core/app.viewAllInit} and [start]{@link module:core/app.start} app in this view.
 * - `modal` - Open destination content in [modal]{@link module:core/view~renderModal} (navigates app [forward]{@link module:core/app.forward})
 * - `closeModal` - [Close modal]{@link module:core/view~closeContentModal} (navigates app [backward]{@link module:core/app.backward})
 * - `forward` / `any` - Navigate app [forward]{@link module:core/app.forward}. This is the default navigation case, so arbitrary `nav` values can be passed (useful for custom analytics nav types)
 *
 * *Note that while this can be passed in explicitly from a referring function, it is sometimes supplied directly from a navigation element through its `data-nav` attribute.*
 *
 * @param {string} [destination] - View ID of destination to force navigation to. If provided, this destination will bypass any current destination queue items (useful for side journeys). Default: `''`
 *
 * @see [`Analytics.navigation`]{@link module:core/analytics.navigation}
 * @see [`renderModal`]{@link module:core/view~renderModal}
 * @see [`renderView`]{@link module:core/view~renderView}
 * @see [`closeContentModal`]{@link module:core/view~closeContentModal}
 * @see [`App.backward`]{@link module:core/app.backward}
 * @see [`App.forward`]{@link module:core/app.forward}
 * @see [`App.start`]{@link module:core/app.start}
 * @see [`App.viewAllInit`]{@link module:core/app.viewAllInit}
 * @see [`Analytics.event`]{@link module:core/analytics.event}
 * @see [`Analytics.recommendEvent`]{@link module:core/analytics.recommendEvent}
 * @see [`App.customize`]{@link module:core/app.customize} `nav` `pre`
 */
export const navigate = (nav = 'start', destination = '') => {
  // set current nav type on app container
  container.attr('data-nav', nav);

  // pre-nav customization
  App.customize('navigate', nav, true);

  App.debug(3, '%cNavigation', 'font-weight:bold; color:cyan;', nav, destination);
  App.debug(1, '----------------------');

  let destinationFn = {
    'restart': () => App.settings.start,
    'view-all': App.viewAllInit,
  };

  let navTypes = {
    default: {
      method: 'forward',
      analytics_event: 'navigation',
    },
    start: {
      method: 'start',
      analytics_event: 'page_view',
    },
    restart: {
      method: 'start',
      direction: 'backward',
    },
    'view-all': {
      method: 'start',
      analytics_event: false,
    },
    back: {
      method: 'backward',
    },
    close_modal: {
      method: 'backward',
    },
  };

  // get current nav def
  let def = Object.assign(navTypes.default, navTypes[nav] || {});
  // set destination if a function exists
  if (destinationFn[nav]) destination = destinationFn[nav]();

  // run app method
  App[def.method](destination);
  // run analytics
  let data = {};
  // if event is navigation, include navigation direction
  if (def.analytics_event === 'navigation') data.navigation_direction = def.direction || def.method;
  // send events
  if (def.analytics_event) Analytics.event(def.analytics_event, data);
  if (App.state.current.location === App.settings.end) Analytics.recommendEvent();

  // post-nav customization
  App.customize('navigate', nav, false);

  // render view
  renderView();
}

/**
 * Handle offsite navigation link click events.
 *
 * Offsite links are `<a>` tags that may link to product pages, direct-to-retailer inventories, or other custom recommendations. In some cases, functionality other than default behavior may be needed, so offsite link clicks are intercepted and processed based on app settings. In addition, successful offsite navigations will trigger an analytics report.
 *
 * **Offsite links must include the class `js-offsiteLink` in order to be properly processed.**
 *
 * ### Disabled links
 *
 * If link is disabled, link navigation is prevented.
 *
 * ### Demo mode
 *
 * If the `demo` [setting]{@link module:core/config.app_settings} is enabled, link navigation is prevented, and an alert with a message is displayed instead. Message text can be modified with a global [label]{@link module:core/config.labels} named `demo_mode_dialog`.
 *
 * ### Offsite disclaimer
 *
 * To display a notice before offsite navigation occurs (to alert the user they will be leaving the app), this functionality can be enabled with the [`offsite_notice`]{@link module:core/config.offsite_notice} settings object. The original link's `href` and analytics meta info is passed through to the notice's "continue" button, so that it will respond as if the original link was clicked.
 *
 * The notice is only displayed once per user session (per load of the app, to be more specific), which is determined by setting a value once it has been displayed (and the user has acknowledged by clicking through).
 *
 * A custom offsite popup partial can be created on a project basis, or the core version `core-popup-offsite.hbs` can be used in a template. Any custom partial must include an outer class of `js-offsitePopup`, and contain a button to continue offsite with the class `js-continueOffsite`.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {object} event - jQuery event object for the clicked link
 *
 * @see [`togglePopup`]{@link module:core/view.togglePopup}
 * @see [`App.updateSettings`]{@link module:core/app.updateSettings}
 * @see [`App.customize`]{@link module:core/app.customize} `data`
 * @see [`Analytics.event`]{@link module:core/analytics.event}
 * @see Used by:
 * @see [**`addEventListeners`**]{@link module:core/view~addEventListeners}
 *
 * @example
 * <!-- offsite link example -->
 * <a href="https://example.com/product" class="js-offsiteLink" data-analytics-meta="sku:3282929">
 *   View product
 * </a>
 *
 * <!-- offsite notice popup example -->
 * <div class="js-offsitePopup js-popup">
 *   <p>This link will take you away from this site.</p>
 *   <div>
 *     <a href="" class="js-continueOffsite js-popupClose">Continue</a>
 *     <button class="js-popupClose">Cancel</button>
 *   </div>
 * </div>
 *
 * <!-- offsite notice popup after activation -->
 * <div class="js-offsitePopup js-popup active">
 *   <p>This link will take you away from this site.</p>
 *   <div>
 *     <a href="https://example.com/product" class="js-continueOffsite js-popupClose" data-analytics-meta="sku:3282929">Continue</a>
 *     <button class="js-popupClose">Cancel</button>
 *   </div>
 * </div>
 *
 * @example
 * <!-- include the default core offsite popup in a template -->
 * {{> core-popup-offsite }}
 */
export const navigateOffsite = (event) => {
  // get current link element
  const link = $(event.currentTarget);

  // if link is disabled, prevent default action and stop here
  if (link.is('[aria-disabled="true"]')) {
    event.preventDefault();
    return;
  }

  // if app is in demo mode or has missing DTR link, show demo mode alert and stop here
  if (App.settings.demo || link.hasClass('js-noRetailerLink')) {
    let alertText = App.settings.labels.demo_mode_dialog;
    event.preventDefault();
    alert(alertText);
    return;
  }

  // get link info
  const navType = link.attr('data-nav');

  // create link data obj
  let data = {
    external_url: link.attr('href'),
  };

  // determine if hatch link
  let isHatch = App.settings.hatch_button.enabled && link.parents(`#_${App.settings.hatch_button.configuration_id}`).length;

  // get product id, if it exists
  const productID = isHatch ? Util.getURLParam('core_id', data.external_url) : link.attr('data-product-id') || '';

  // handle offsite link
  if (link.hasClass('js-offsiteLink')) {
    // set link target, if supplied
    if (App.settings.offsite_link_target) link.attr('target', App.settings.offsite_link_target);
    // show offsite disclaimer, if needed
    if (App.settings.offsite_notice.enabled && !App.settings.offsite_notice.shown) {
      // prevent navigation
      event.preventDefault();
      // add original link's href, plus analytics data, to continue button
      container.find('.js-continueOffsite').attr({
        href: data.external_url,
        'data-product-id': productID,
      });
      // open popup
      const popup = container.find('.js-offsitePopup');
      togglePopup(true, popup);
      return;
    }
  }

  // handle offsite continue, i.e. continuing from the disclaimer
  // store that a disclaimer has been shown
  if (link.hasClass('js-offsiteContinue')) {
    App.updateSettings({offsite_notice: {shown: true}});
  }

  // allow project customization
  App.customize('navigateOffsite', data);

  // if we've gotten this far, link navigation is occurring
  // send analytics events
  if (productID) Analytics.ecommerceEvent('select_item', productID);
  if (navType === 'direct-to-retailer') Analytics.recommendEvent();
  Analytics.event('external_click', data);
};

/**
 * Manage input interaction and post-interaction functionality.
 *
 * All input interactions should be routed through this method - inputs should never be selected/deselected directly (e.g. by removing `active` class or other state modifications), as this may cause issues with maintaining an up-to-date app state.
 *
 * 1. If input is disabled, do nothing.
 * 1. Each input type has its own state handler, which is chosen by the input's `data-input-type` attribute. See individual input handlers for more information on input state handling.
 * 1. [Set state for "clear field" button]{@link module:core/form.setClearFieldState} for this group (either visible or hidden).
 * 1. In addition to handling input and field state, certain post-interaction functionality may also be run:
 *     - **App state updates** - unless `passive` parameter is enabled, the app's internal state [will be updated]{@link module:core/view.updateState} with view information.
 *     - **Facet filtering** - if the input is a facet input, [facet filtering]{@link module:core/view.facet.filter} will be run.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {jQuery} input - Current input jQuery object.
 *
 * @param {boolean} [deselect] - When enabled, deselection will be forced for the input. This applies even to inputs that are not normally un-selectable (e.g. radio + select). Default: `false`
 *
 * @param {boolean} [passive] - When enabled, state updates will not occur. This may be used to limit unnecessary processing when running on multiple inputs at once, or to prevent callback loops (e.g. when handling conditionals). Default: `false`
 *
 * @see [`Form.text.setState`]{@link module:core/form.text.setState}
 * @see [`Form.button.setState`]{@link module:core/form.button.setState}
 * @see [`Form.select.setState`]{@link module:core/form.select.setState}
 * @see [`Form.radio.setState`]{@link module:core/form.radio.setState}
 * @see [`Form.checkbox.setState`]{@link module:core/form.checkbox.setState}
 * @see [`Form.setClearFieldState`]{@link module:core/form.setClearFieldState}
 * @see [`updateState`]{@link module:core/view.updateState}
 * @see [`facet.filter`]{@link module:core/view.facet.filter}
 * @see [`App.customize`]{@link module:core/app.customize} `input` `deselect` `passive`
 */
export const inputActions = (input, deselect = false, passive = false) => {
  // do nothing if disabled
  if (input.is('[aria-disabled="true"]')) return;

  // input settings
  const currentType = input.attr('data-input-type');
  const allowDeselect = input.is('[data-allow-deselect]');
  const facetInput = input.is('[data-facet]');

  // run input set function
  Form[currentType].setState(input, deselect, passive);

  // set clear field state, if applicable
  if (allowDeselect || facetInput) Form.setClearFieldState(input.closest('.js-field'));

  // run project-specific actions
  App.customize('inputActions', input, deselect, passive);

  // post input actions, if not passive mode
  if (!passive) updateState();
}

/**
 * Keep app state updated with data related to the current view.
 *
 * - [Current path node]{@link module:core/view.updateNodeState} is updated with view data.
 * - [Conditionally-displayed elements are handled]{@link module:core/view.handleConditionals}.
 * - [Continue button is enabled/disabled]{@link module:core/view.setNavigationState} based on view conditions.
 * - [Form submit button is enabled/disabled]{@link module:core/form.setSubmitState} based on form conditions.
 * - [Container attribute values are updated]{@link module:core/view~updateAppAttributes}.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @see [`updateNodeState`]{@link module:core/view.updateNodeState}
 * @see [`handleConditionals`]{@link module:core/view.handleConditionals}
 * @see [`facet.filter`]{@link module:core/view.facet.filter}
 * @see [`Form.repeater.setState`]{@link module:core/form.repeater.setState}
 * @see [`setNavigationState`]{@link module:core/view.setNavigationState}
 * @see [`Form.setSubmitState`]{@link module:core/form.setSubmitState}
 * @see [`reloadProducts`]{@link module:core/view~reloadProducts}
 * @see Used by:
 * @see [**`inputActions`**]{@link module:core/view.inputActions}
 * @see [**`postRenderViewActions`**]{@link module:core/view~postRenderViewActions}
 * @see [**`facet.reset`**]{@link module:core/view.facet.reset}
 * @see [**`Form.disableInputs`**]{@link module:core/form.disableInputs}
 */
export const updateState = () => {
  // update current node with selection info and attributes
  updateNodeState();
  // run conditional logic
  handleConditionals();
  // update facet state
  facet.filter();
  // update repeater state
  Form.repeater.setState();
  // update navigation state
  setNavigationState();
  // update form state
  Form.setSubmitState();
  // reload products if necessary
  reloadProducts();
}

/**
 * Keep app [`path`]{@link module:core/app~path} node data up-to-date with current view state information. This method is run during input interactions and after each render to capture view state changes.
 *
 * - [`config.activeOptions`]{@link module:core/view.config.activeOptions} are updated.
 * - [App attributes are updated]{@link module:core/app.setAttributes}.
 * - The following [node]{@link module:core/app~nodeInit} properties are updated:
 *     - `selections` - Array of input IDs representing active inputs.
 *     - `dq` - The current destination queue order.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @see [`App.setAttributes`]{@link module:core/app.setAttributes}
 * @see [`App.updatePathNode`]{@link module:core/app.updatePathNode}
 * @see [`App.getDestinationQueue`]{@link module:core/app.getDestinationQueue}
 * @see [`getDestinations`]{@link module:core/view~getDestinations}
 * @see Used by:
 * @see [**`updateState`**]{@link module:core/view.updateState}
 * @see [**`handleConditionals`**]{@link module:core/view.handleConditionals}
 *
 * @example
 * // selections and destinations that will be added to current node
 * {
 *   selections = ['input_1_id', 'input_4_id'];
 *   dq = ['productivity', 'entertainment'];
 * }
 */
export const updateNodeState = () => {
  // locate active inputs and get option content for each
  config.activeOptions = getElementData(getActiveInputs().toArray());
  // update app attributes
  App.setAttributes();
  // add current selections and destination queue
  App.updatePathNode({
    selections: config.activeOptions.map(obj => obj.id),
    dq: App.getDestinationQueue(getDestinations()),
  });
}

/**
 * Show or hide conditional elements based on selections made within the current view. Conditionally displayed elements can be defined using [`conditional_on`]{@link module:core/config.conditional_on} settings for content objects that support them.
 *
 * An overview of this process:
 *
 * 1. Find all elements that require conditional display within the current `container`. Conditional elements are selected with the data attribute `data-conditional`.
 *
 * 2. Each element's `conditions` are then checked against active view attributes (retrieved from `[App.state]{@link module:core/app.state}.current.attr`) to see if they should be displayed. If conditions for a given element are not met, it will be hidden. If element is an active input, it will be deselected using [`Form.deselectInputs`]{@link module:core/form.deselectInputs}.
 *
 * 3. If the conditional element has a containing fieldset, the fieldset is checked to verify that it has visible items within it. If not, it is hidden as well.
 *
 * 4. After all conditional elements have been processed, if any input states have been modified, the current node state is updated.
 *
 * *Note that global conditionals are not managed by this method. See [`App.transformContent`]{@link module:core/app~transformContent} for more information on how globals conditionals are handled.*
 *
 * @method
 *
 * @since 3.0.0
 *
 * @see [`getElementData`]{@link module:core/view.getElementData}
 * @see [`App.conditionalOn`]{@link module:core/app.conditionalOn}
 * @see [`activeInputs`]{@link module:core/view.getActiveInputs}
 * @see [`Form.deselectInputs`]{@link module:core/form.deselectInputs}
 * @see [`updateNodeState`]{@link module:core/view.updateNodeState}
 * @see [`setNavigationState`]{@link module:core/view.setNavigationState}
 * @see [`App.customize`]{@link module:core/app.customize} `conditionalFields` `inputsChanged`
 * @see Used by:
 * @see [**`updateState`**]{@link module:core/view.updateState}
 *
 * @example
 * // element display conditions
 * [
 *   {
 *     type: 'filter',
 *     name: 'form_factor',
 *     value: 'notebook',
 *   }
 * ]
 *
 * // active view attr from selections (condensed attributes)
 * attr: {
 *   filter: {
 *     form_factor: 'notebook',
 *   }
 * }
 *
 * // expected result: element will be displayed
 */
export const handleConditionals = () => {
  // conditional fields
  let conditionalFields = container.find('[data-conditional]');
  // if any inputs are deselected, we'll need to update state
  let inputsChanged = false;

  // check each conditional field to see if conditions have been met
  conditionalFields.each((i, e) => {
    let field = $(e);
    // get containing fieldset, no matter the field type
    let fieldset = field.closest('.js-field');
    // get field conditions
    let conditions = getElementData(field).conditional_on;
    // find any fail conditions in non-global conditions
    let conditionsMet = App.conditionalOn(conditions);

    // toggle hidden class on field
    if (conditionsMet) progressiveDelay(true, field);
    field.toggleClass('hidden', !conditionsMet);

    // if conditions are not met, deactivate any contained inputs
    if (!conditionsMet) {
      // collect any active inputs
      let activeInputs = field.is('.input.active') ? field : getActiveInputs(field);
      // deselect inputs (passively) and add number of active inputs to inputsChanged
      if (activeInputs.length) {
        Form.deselectInputs(activeInputs, true);
        inputsChanged = true;
      }
    }

    // hide fieldsets that have no visible inputs
    const visibleInputs = fieldset.find('li').not('.hidden');
    if (!visibleInputs.length) fieldset.addClass('hidden').attr('data-conditional-parent', '');
    else if (fieldset.is('[data-conditional-parent].hidden')) fieldset.removeClass('hidden');
  });

  // run project-specific actions
  App.customize('handleConditionals', conditionalFields, inputsChanged);

  // if we've changed states of any inputs, update node state and run conditionals again
  if (inputsChanged) {
    updateNodeState();
    handleConditionals();
  }
}

/**
 * Handle the state of the primary navigation, or "continue", button. [Direct-to-retailer link]{@link module:core/view~setDTRLink} is set during this step, if enabled.
 *
 * Forward navigation is only enabled under certain conditions. This method enables/disables the navigation button (or navigates forward automatically) based on current view conditions:
 *
 * - If required fields are not fulfilled, **navigation is disabled**.
 *
 * - If no selections have been made, but the `allow_continue` [page setting]{@link module:core/config.page_settings} *is* enabled in the current view, **navigation is enabled**.
 *
 * - If selections have been made, and the `auto_continue` [page setting]{@link module:core/config.page_settings} is *not* enabled, **navigation is enabled**.
 *
 * - If selections have been made, and `auto_continue` *is* enabled in the current view, **navigation will occur immediately**.
 *
 * - In all other cases, **navigation is disabled**.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @see [`setDTRLink`]{@link module:core/view~setDTRLink}
 * @see [`App.currentNodeSetting`]{@link module:core/app.currentNodeSetting}
 * @see [`getActiveInputs`]{@link module:core/view.getActiveInputs}
 * @see [`requiredFields`]{@link module:core/view.requiredFields}
 * @see [`toggleContinue`]{@link module:core/view.toggleContinue}
 * @see [`navigate`]{@link module:core/view.navigate}
 * @see Used by:
 * @see [**`updateState`**]{@link module:core/view.updateState}
 */
export const setNavigationState = () => {
  // update DTR link, if necessary
  setDTRLink();
  // get nav-relevant settings
  const active = !!config.activeOptions.length;
  const auto = App.currentNodeSetting('auto_continue') && getActiveInputs(container, '.js-buttonOption').length;
  const allow = App.currentNodeSetting('allow_continue');
  // if required fields exist, check that all required fields have been fulfilled
  // assume requirements are met, as we are looking for fail cases
  let reqs = true;
  // if there are any inactive required fields, fail
  if (requiredFields().length) reqs = false;

  // cases to enable navigation
  let enable = reqs && ((!active && allow) || (active && !auto));

  // navigate or toggle continue
  auto ? navigate('forward') : toggleContinue(enable);
}

/**
 * Enable or disable forward navigation, i.e. the "continue" button.
 *
 * The "continue" button is any button that contains a `data-nav="forward"` attribute, or `data-nav="direct-to-retailer"` if [direct-to-retailer]{@link module:core/config.direct_to_retailer} functionality is enabled. It may apply to in-app navigation buttons or the external links (as in the case of direct-to-retailer functionality).
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {boolean} [enable] - Enable or disable continue. Default: `false`
 *
 * @see Used by:
 * @see [**`setNavigationState`**]{@link module:core/view.setNavigationState}
 */
export const toggleContinue = (enable = false) => {
  const continueButton = container.find('[data-nav="forward"], [data-nav="direct-to-retailer"]');

  // set button state
  continueButton.attr('aria-disabled', !enable).prop('disabled', !enable);
}

/**
 * Get the current view's active uncondensed [`attributes`]{@link module:core/config.attributes} for a given attribute type. This includes:
 *
 * - Attributes for selected or "active" inputs.
 * - Attributes attached to the current view.
 * - Attributes for inactive text inputs.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {object} [attrDef] - [Attribute definition]{@link module:core/app.attributes} to use for attribute retrieval. Default: [app primary attribute]{@link module:core/app~getPrimaryAttribute}
 *
 * @return {array|object}
 *
 * @see [`inputAttributes`]{@link module:core/view.inputAttributes}
 * @see Used by:
 * @see [**`App.getAttributesFrom`**]{@link module:core/app.getAttributesFrom}
 *
 * @example
 * // simplified view example with selection buttons
 * <div data-view>
 *   <button class="input active">Organizing photos online</button>
 *   <button class="input">Editing images</button>
 *   <button class="input active">Video editing</button>
 * </div>
 *
 * // only attributes for the inputs with the "active" class,
 * // plus any attributes attached to the "view" div, would be retrieved.
 */
export const activeAttributes = (attrDef = App.settings.primary_attribute) => {
  // copy active options
  let options = config.activeOptions.slice();
  // add view attributes to options, if they exist
  if (App.current.attributes) options.push({attributes: App.current.attributes});
  // get any inactive text attributes
  const inactiveText = getElementData(container.find('.js-textInput').not('.active').toArray());

  return inputAttributes([...options, ...inactiveText], attrDef);
}

/**
 * Gather attributes from a group of inputs.
 *
 * This method [retrieves attributes]{@link module:core/util.parseElementAttributes} associated with provided inputs. Depending on the value of the `condense` param, this function will either return an array of uncondensed attribute objects, or an object with attribute values condensed according to the attribute's definition in [`App.attributes`]{@link module:core/app.attributes}.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {array|jQuery} inputs - Inputs to retrieve attributes from. Valid `inputs` values include:
 *
 * - jQuery object containing input selections
 * - Array of HTML input elements
 * - Array of JSON option objects
 *
 * Attributes are retrieved from inputs using [`Util.parseElementAttributes`]{@link module:core/util.parseElementAttributes}.
 *
 * @param {object} [attrDef] - [Attribute definition]{@link module:core/app.attributes} to use for attribute retrieval. Default: [app primary attribute]{@link module:core/app~getPrimaryAttribute}
 *
 * @param {boolean} [condense] - Whether attributes should be condensed or left as attribute arrays. See [`Util.condenseAttributes`]{@link module:core/util.condenseAttributes} for more information on condensed vs. uncondensed attributes. Default: `false`
 *
 * @return {array|object}
 *
 * @see Used by:
 * @see [**`App.getAttributesFrom`**]{@link module:core/app.getAttributesFrom}
 * @see [**`activeAttributes`**]{@link module:core/view.activeAttributes}
 * @see [**`facet.disableUnavailable`**]{@link module:core/view.facet.disableUnavailable}
 * @see [**`facet.setLowestPrice`**]{@link module:core/view.facet.setLowestPrice}
 *
 * @example
 * // input data
 * let inputs = [
 *   {
 *     label: 'Organizing photos online',
 *     attributes: [
 *       {
 *         types: ['filter', 'analytics'],
 *         name: 'processor',
 *         value: 'pentium',
 *         priority: '6',
 *       },
 *       {
 *         types: ['analytics'],
 *         name: 'usage_model',
 *         value: 'photo',
 *       },
 *     ],
 *   },
 *   {
 *     label: 'Video editing',
 *     attributes: [
 *       {
 *         types: ['filter', 'analytics'],
 *         name: 'processor',
 *         value: 'i7',
 *         priority: '3',
 *       },
 *       {
 *         types: ['filter', 'analytics'],
 *         name: 'storage_amount',
 *         value: 500,
 *       },
 *       {
 *         types: ['analytics'],
 *         name: 'usage_model',
 *         value: 'video',
 *       },
 *     ],
 *   },
 * ]
 *
 * @example
 * // filter attribute definition
 * let filterDef = {
 *   type: 'filter',
 *   allowMultiple: false,
 * }
 *
 * // gather filter attributes
 * inputAttributes(filterDef); // uncondensed
 * inputAttributes(filterDef, true); // condensed
 *
 * // uncondensed output
 * [
 *   {
 *     name: 'processor',
 *     value: 'i7',
 *   },
 *   {
 *     name: 'storage_amount',
 *     value: 500,
 *   },
 * ]
 *
 * // condensed output
 * {
 *   processor: 'i7',
 *   storage_amount: 500,
 * }
 *
 * @example
 * // analytics attribute definition
 * let analyticsDef = {
 *   type: 'analytics',
 *   allowMultiple: true,
 * }
 *
 * // gather analytics attributes
 * inputAttributes(analyticsDef); // uncondensed
 * inputAttributes(analyticsDef, true); // condensed
 *
 * // uncondensed output
 * [
 *   {
 *     name: 'processor',
 *     value: 'i7',
 *   },
 *   {
 *     name: 'storage_amount',
 *     value: 500,
 *   },
 *   {
 *     name: 'usage_model',
 *     value: 'photo',
 *   },
 *   {
 *     name: 'usage_model',
 *     value: 'video',
 *   },
 * ]
 *
 * // condensed output
 * {
 *   processor: ['i7'],
 *   storage_amount: [500],
 *   usage_model: ['photo', 'video'],
 * }
 */
export const inputAttributes = (inputs, attrDef = App.settings.primary_attribute, condense = false) => {
  // create an array to hold found attributes
  let attributes = [];
  // this each statement accepts arrays of jquery objects, to accommodate the need for manual sorting (other than DOM order)
  $.each(inputs, (i, element) => {
    // get all attributes of a given type for this input
    let inputAttr = Util.parseElementAttributes(element, attrDef);
    // if input has attributes for this type, add them to final attributes array
    if (inputAttr.length) attributes.push(...inputAttr);
  });
  // condense, if necessary
  if (condense) attributes = Util.condenseAttributes(attributes, attrDef);

  return attributes;
}

/**
 * Determine if attributes attached to provided input(s) are active in the current app state.
 *
 * - Attributes are retrieved from the app state (see `preAnchor` param for details).
 * - For each provided `attrDefs`:
 *     - [Get input attributes]{@link module:core/view.inputAttributes}. Skip to next attribute def if none of this type are present.
 *     - Check each input attribute object against path attributes of the same type using [`Util.objectIncludes`]{@link module:core/util.objectIncludes}. Note that the only properties from the input attribute object that are used for comparison are `name`, `value`, and `block`, to avoid issues matching priorities or other attribute metadata that may not be present in the path attribute.
 *
 * If all input attributes have path attribute matches, an array of all matched path attributes is returned. If no match was found for any input attribute, an empty array is returned (this should be considered the "fail" case).
 *
 * *Note that if multiple inputs are provided, all attributes from all inputs will need to be matched in order to constitute a "positive" match.*
 *
 * @method
 *
 * @since 3.4.0
 *
 * @param {array|jQuery} inputs - Inputs to check. See [`inputAttributes`]{@link module:core/view.inputAttributes} param `inputs` for a complete description of valid formats.
 *
 * @param {array} attrDefs - Array of attribute type definitions to check input attributes against. Default: [`App.attributes`]{@link module:core/app.attributes}
 *
 * @param {boolean} preAnchor - When enabled, path attributes from the `preAnchor` [app state property]{@link module:core/app.state} property will be used instead of the current state attributes.
 *
 * @return {array}
 *
 * @see [`inputAttributes`]{@link module:core/view.inputAttributes}
 * @see Used by:
 * @see [**`applyPastSelections`**]{@link module:core/view~applyPastSelections}
 */
export const inputAttributeMatch = (inputs, attrDefs = App.attributes, preAnchor = false) => {
  // hold matched path attributes
  let matchedAttributes = [];
  // define path attribute source
  let pathAttributeSrc = preAnchor && App.state.preAnchor ? App.state.preAnchor : App.state;
  // loop through valid attribute defs
  for (let attrDef of attrDefs) {
    // get input attributes for this attr type
    const optionAttributes = inputAttributes(inputs, attrDef);
    // if option has no attributes of this type, skip
    if (!optionAttributes.length) continue;
    // get relevant path attributes
    const pathAttributes = pathAttributeSrc.attributes[attrDef.type];
    const pathAttr = pathAttributeSrc.attr[attrDef.type];
    // determine if all option attributes of this type are included in input
    for (let optionAttribute of optionAttributes) {
      // create object to match to path attribute
      let obj = Object.keys(optionAttribute).reduce((obj, key) => ['name', 'value', 'block'].includes(key) ? Object.assign(obj, {[key]: optionAttribute[key]}) : obj, {});
      // initialize match variable
      let match = false;
      // if match is found, set the match state to true
      for (let p of pathAttributes) {
        // skip this attribute if a priority exists, and the attribute value is not the correct priority value
        let formatted = Util.formatAttributes({[p.name]: p.value}, attrDef)[p.name];
        if (p.hasOwnProperty('priority') && !App.attributeMatch(p.name, pathAttr[p.name], formatted, attrDef)) continue;
        // compare objects
        if (Util.objectIncludes(obj, p, true)) {
          // add matched path attribute
          matchedAttributes.push(p);
          // set match to true
          match = true;
          break;
        }
      }
      // if no match is found for any option attribute, fail and stop here
      if (!match) return [];
    }
  }
  // return all matched attributes
  return matchedAttributes;
};

/**
 * Find active inputs within the view or supplied jQuery object. Additional selectors to narrow results may be supplied.
 *
 * Returns a jQuery object containing active inputs.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {jQuery} [field] - JQuery object to find active inputs within. Default: [`container`]{@link module:core/view.container}
 * @param {string} [selectors] - Additional selector(s) to be appended to the default active inputs selectors. Default: `''`
 *
 * @return {jQuery}
 *
 * @example
 * // example selection buttons inside a container
 * <div class="field_name">
 *   <button id="input_1" class="input active" data-facet>Organizing photos online</button>
 *   <button id="input_2" class="input" data-facet>Editing images</button>
 *   <button id="input_3" class="input active">Video editing</button>
 *   <button id="input_4" class="input">Editing images</button>
 * </div>
 *
 * @example
 * // get the containing field
 * let fieldName = $('.field_name');
 *
 * // get active inputs within field
 * // note that using the default field (container) would yield the same result in this case
 * getActiveInputs(fieldName, `[data-facet]`);
 *
 * // expected output: jQuery object containing element with id "input_1"
 */
export const getActiveInputs = (field = container, selectors = '') => field.find(`.input.active${selectors}`);

/**
 * Find any required inputs that have not yet been selected within the view or supplied jQuery object. Additional selectors to narrow results may be supplied.
 *
 * Returns a jQuery object containing either only missing required inputs (default), or all required inputs, depending on value of `unmet`.
 *
 * @method
 *
 * @since 3.4.0
 *
 * @param {jQuery} [field] - JQuery object to find inputs within. Default: [`container`]{@link module:core/view.container}
 *
 * @param {string} [selectors] - Additional selector(s) to be appended to the default input selectors. Default: `''`
 *
 * @param {boolean} [unmet] - When enabled, only missing required inputs will be returned. Default: `true`
 *
 * @return {jQuery}
 */
export const requiredFields = (field = container, selectors = '', unmet = true) => {
  // get all required fields
  let required = field.find('[data-required]').not('.hidden, .ignore');
  // if returning all, stop here
  if (!unmet) return required;
  // return fields with missing requirements
  return required.filter((i, e) => !getActiveInputs($(e), selectors).length);
}

/**
 * Get the "ID" value from an element. This will either return the semantic element `id`, or a `data-id` attribute, if the semantic `id` does not exist.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {HTMLElement|jQuery} element - JQuery or HTML element with an `id` or `data-id` attribute.
 *
 * @return {string}
 */
export const getElementID = (element) => {
  element = $(element)[0];
  return element.dataset.hasOwnProperty('view') ? '' : element.id || element.dataset.id;
}

/**
 * Get content data associated with element or array of elements, as long as they have a properly-formatted ID.
 *
 * Returns a single object if a single element is provided, or an array of objects if an array of elements is provided.
 *
 * Many elements utilize an `id` or `data-id` value which (when [decoded]{@link module:core/util.decodeID}) acts as a "location" or path to that object within the content JSON. With this path, the content object can be retrieved using [`Util.getObjectValue`]{@link module:core/util.getObjectValue}.
 *
 * More information on how these path IDs are generated can found in [`App.transformContent`]{@link module:core/app~transformContent}.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {HTMLElement|array<HTMLElement>} elements - Element or array of elements to retrieve data for. Must have an `id` or `data-id` value in the path-like format created by [`App.transformContent`]{@link module:core/app~transformContent}.
 *
 * @return {object|array}
 *
 * @see [`getElementID`]{@link module:core/view.getElementID}
 *
 * @example
 * // element with encoded path-like ID
 * let button = <button id="srt-Yi1idS1idg">Editing images</button>
 *
 * // get button data
 * getElementData(button);
 *
 * // expected output
 * {
 *   label: 'Editing images',
 *   id: 'content-0-input_object',
 *   destination: 'usage_model',
 *   attributes: {
 *     ...
 *   }
 * }
 */
export const getElementData = (elements) => {
  let isArray = Array.isArray(elements);
  // shorten data-retrieval function
  let get = (e) => Util.getObjectValue(App.current, Util.decodeID(getElementID(e)));
  // return array of data objects, or a single data object
  return isArray ? elements.map(element => get(element)) : get(elements);
};

/**
 * Retrieve an input (as a jQuery item) by either `id` or `data-id` attributes.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {string} id - The `id` or `data-id` value to select for. Note this will *only* select elements with the class `input`.
 *
 * @return {jQuery}
 */
export const getInputByID = (id) => container.find(`#${id}.input, .input[data-id="${id}"]`);

/**
 * Trap or release keyboard tab focus within a provided element. Forces keyboard tab navigation to loop through all focusable elements within the element.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {jQuery} field - jQuery element to trap focus in.
 *
 * @param {boolean} [trap] - Whether to trap focus or release it. Default: `false`
 *
 * @see Used by:
 * @see [**`renderModal`**]{@link module:core/view~renderModal}
 * @see [**`closeContentModal`**]{@link module:core/view~closeContentModal}
 */
export const toggleTrapFocus = (field, trap = false) => {
  // trap focus
  if (trap) {
    const focusable = field.find(':visible:focusable').not('#page_title');
    const firstElement = focusable.first();
    const lastElement = focusable.last();
    firstElement.focus();
    field.keydown((e) => {
      if (e.keyCode === keycode.tab && focusable.length === 1) e.preventDefault();
      // if tabbing forwards, and on last element, wrap around to first element
      if (e.keyCode === keycode.tab && !e.shiftKey && document.activeElement === lastElement[0] && focusable.length > 1) {
        firstElement.focus();
        e.preventDefault();
      }
      // if tabbing backwards, and on first element, wrap around to last element
      if (e.keyCode === keycode.tab && e.shiftKey && document.activeElement === firstElement[0] && focusable.length > 1) {
        lastElement.focus();
        e.preventDefault();
      }
    });
  }

  // release focus
  else field.unbind('keydown');
}

/**
 * Toggle the "click catch" layer.
 *
 * The click catch is an element spanning the entire view, which is used as a trigger to close modals or otherwise expandable elements by clicking somewhere other than on the element itself.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {boolean} activate - Activate or deactivate click catch. Default: `false`
 *
 * @param {...string} [modifierClass] - Customization class(es). Can be used to change the click catch's behavior or appearance based on a particular action or interaction with an element. The click catch element can be targeted in CSS with `.u-clickCatch` + any custom selectors provided here.
 *
 * @see Used by:
 * @see [**`togglePopup`**]{@link module:core/view.togglePopup}
 * @see [**`closeModals`**]{@link module:core/view.closeModals}
 * @see [**`renderModal`**]{@link module:core/view~renderModal}
 * @see [**`closeContentModal`**]{@link module:core/view~closeContentModal}
 * @see [**`preRenderViewActions`**]{@link module:core/view~preRenderViewActions}
 * @see [**`Form.select.expand`**]{@link module:core/form.select.expand}
 * @see [**`Form.select.collapse`**]{@link module:core/form.select.collapse}
 */
export const toggleClickCatch = (activate = false, ...modifierClass) => {
  // activate
  if (activate) {
    let classes = modifierClass || [];
    classes.push('active');
    $('.u-clickCatch').addClass(classes.join(' '));
  }
  // deactivate
  else {
    $('.u-clickCatch').attr('class', 'u-clickCatch js-closeModal');
  }
}

/**
 * Activate or deactivate a jQuery popup element.
 *
 * If a popup element is provided it will be "activated", otherwise any active popups will be deactivated. Toggles an `active` class on provided field and toggles the [click catch]{@link module:core/view.toggleClickCatch}, depending which is needed.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {boolean} [activate] - Activate or deactivate popup. Default: `false`
 *
 * @param {string|jQuery|HTMLElement} [target] - jQuery element, HTML element, or string selector of element to target for activation or deactivation. Default: `.js-popup`
 *
 * *While this parameter is technically optional in all cases, if no target is provided, all elements with the class `.js-popup` will be targeted. This may not be the intended effect during activation.*
 *
 * @see [`toggleClickCatch`]{@link module:core/view.toggleClickCatch}
 * @see [`App.customize`]{@link module:core/app.customize}
 * @see Used by:
 * @see [**`closeModals`**]{@link module:core/view.closeModals}
 * @see [**`addEventListeners`**]{@link module:core/view~addEventListeners}
 */
export const togglePopup = (activate = false, target = '.js-popup') => {
  let popup = $(target);
  popup.toggleClass('active', activate);
  toggleClickCatch(activate);
};

/**
 * Catch-all function to close any possible modals that might be open.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @see [`togglePopup`]{@link module:core/view.togglePopup}
 * @see [`closeContentModal`]{@link module:core/view~closeContentModal}
 * @see [`Form.select.collapse`]{@link module:core/form.select.collapse}
 * @see [`App.customize`]{@link module:core/app.customize}
 * @see Used by:
 * @see [**`addEventListeners`**]{@link module:core/view~addEventListeners}
 */
export const closeModals = () => {
  Form.select.collapse();
  togglePopup();
  closeContentModal();

  // run project-specific actions
  App.customize('closeModals');
}

/**
 * Render current content to a target jQuery or HTML element.
 *
 * If no template partial or content object are provided, the method utilizes [`App.current`]{@link module:core/app.current} content to render a [page]{@link module:core/config.page} template to a given target element.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @param {HTMLElement|jQuery} target - jQuery or DOM element to render content to.
 *
 * @param {string} [template] - Name of the template or partial to render content with. Default: `template-[App.current.template]`
 *
 * @param {object} [content] - Object to use as the data source for the template partial. Default: `App.current`
 *
 * @param {boolean} [append] - When enabled, content will be appended to `target` element instead of replacing its contents. Default: `false`
 *
 * @see Used by:
 * @see [**`renderAppElements`**]{@link module:core/view~renderAppElements}
 * @see [**`addEventListeners`**]{@link module:core/view~addEventListeners}
 * @see [**`renderView`**]{@link module:core/view~renderView}
 * @see [**`renderModal`**]{@link module:core/view~renderModal}
 * @see [**`addImageBuildInfo`**]{@link module:core/view~addImageBuildInfo}
 */
export const renderContentTo = (target, template = `template-${App.current.template}`, content = App.current, append = false) => {
  target = $(target);
  let renderedContent = Handlebars.templates[template](content);
  append ? target.append(renderedContent) : target.html(renderedContent);
};

/**
 * Render QR code to a specified container. Generates a canvas element with the QR code, appended to the provided `selector`.
 *
 * To enable QR code functionality, the `QRCode` module must be imported in the project or customization's `main.js` file, as a setting at `App.settings.view.QRCode`. See examples below for implementation details.
 *
 * @method
 *
 * @since 3.4.0
 *
 * @param {string} string - String to turn into a QR code.
 *
 * @param {jQuery} [element] - The element to render the QR code canvas to. Default: `container.find('.js-qrCode')`
 *
 * @param {object} [options] - Render options for QR code. Accepts any valid [`qrcode` module options]{@link https://www.npmjs.com/package/qrcode#qr-code-options}. Note that supplying these directly will override global options.
 *
 * @see Used by:
 * @see [**`addEventListeners`**]{@link module:core/view~addEventListeners}
 *
 * @example
 * // import example
 * // include QR code module in customization main.js
 * import QRCode from 'qrcode';
 *
 * // start app with QRCode supplied in config
 * let config = {
 *   view: {QRCode},
 * };
 * $(document).ready(function() {
 *   init(config);
 * });
 *
 * @example
 * <!-- include qr code popup partial in view template -->
 * <!-- or, create a custom project partial and provide a custom qr code selector -->
 * {{> core-qrcode }}
 *
 * @example
 * // usage example
 * let options = {
 *   scale: 12,
 *   margin: 8,
 * };
 *
 * renderQRCode('https://example.com', '.js-qrCode', options);
 */
export const renderQRCode = (string, element = container.find('.js-qrCode'), options = {}) => {
  // do nothing if QRCode is not enabled
  if (!App.settings.view.QRCode.enabled) return;
  // stop here if canvas container is not found
  if (!element.length) return;
  // merge in any supplied options
  options = Object.assign({}, App.settings.view.QRCode.options, options);
  // remove any existing canvas elements
  element.find('canvas').remove();
  // render QR code
  App.settings.view.QRCode.toCanvas(string, options, function(error, canvas) {
    if (error) console.error(error);
    else element.append(canvas);
  });
};

/**
 * Set focus at the start of the view.
 *
 * The focus point is either the view's `h1` element, or if one does not exist, the first focusable element in the view.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @see [`App.customize`]{@link module:core/app.customize}
 * @see Used by:
 * @see [**`closeContentModal`**]{@link module:core/view~closeContentModal}
 * @see [**`postRenderViewActions`**]{@link module:core/view~postRenderViewActions}
 * @see [**`facet.reset`**]{@link module:core/view.facet.reset}
 */
export const resetPageFocus = () => {
  let focalPoint = container.find('h1');
  if (!focalPoint.length) {
    focalPoint = container.find(':visible:focusable').first();
  }
  focalPoint.focus();
}

/**
 * Scroll to top of page (or app), unless `scroll_to_top` [app setting]{@link module:core/config.app_settings} is disabled. The scroll position may also be offset to account for fixed retailer headers with the `scroll_offset` setting.
 *
 * On non-iFrame builds, this method simply uses jQuery's `scrollTop` method to scroll to the top of the app's HTML to the desired .
 *
 * On iFrame builds, in order to scroll to an element from "outside" of the iframe, the DOM element method [scrollIntoView()]{@link https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollIntoView} must be used on an element at the top of the view. This element, with `id="srt-scrollTopAnchor"`, is generated during [`renderAppElements`]{@link module:core/view~renderAppElements} on relevant builds.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @see Used by:
 * @see [**`renderModal`**]{@link module:core/view~renderModal}
 * @see [**`postRenderViewActions`**]{@link module:core/view~postRenderViewActions}
 */
export const scrollToTop = () => {
  // do nothing if scroll to top is disabled
  if (!App.settings.scroll_to_top) return;

  // scroll iframe parent page
  if (App.settings.platform === 'iframe') $('#srt-scrollTopAnchor')[0].scrollIntoView({
    behavior: 'auto',
    block: 'start',
  });

  // scroll app html
  else $('html').scrollTop(App.settings.scroll_offset);
}

/**
 * Change the language of the current view.
 *
 * - Update HTML `lang` attribute with current language code.
 * - Update HTML `dir` attribute with either `ltr` or `rtl`.
 * - [Add link to language-specific css files]{@link module:core/view~updateLangStyles}, if necessary.
 * - Update site title and description.
 *
 * @method
 *
 * @since 3.2.0
 *
 * @see Used by:
 * @see [**`renderAppElements`**]{@link module:core/view~renderAppElements}
 */
export const updateLang = () => {
  let lang = App.settings.lang;
  let dir = App.settings.lang_dir;

  // update html attributes
  $('html').attr({lang, dir});

  // update lang css
  updateLangStyles();

  // update site title and description
  $('title').html(App.settings.site_title);
  $('meta[name="ogTitle"]').attr('content', App.settings.site_title);
  $('meta[name="ogDescription"]').attr('content', App.settings.site_description);
};

/**
 * Control the play state of an HTML5 audio or video element.
 *
 * If the media element's direct parent is a `.video-wrapper` element, the class `playing` is toggled on it, depending on the play state. This allows for state-dependent CSS styling.
 *
 * @method
 *
 * @since 3.2.5
 *
 * @param {HTMLElement} media - The audio or video element.
 * @param {boolean} [play] - When `true`, the media state will be set to "play", and when `false` the media state will be set to "paused". If no desired state is provided, the media element's current play state will be toggled, i.e. if paused, it will play, and if playing, it will be paused.
 */
export const toggleMedia = (media, play) => {
  if (typeof play === 'undefined') play = media.paused;
  play ? media.play() : media.pause();
  $(media).parent('.video-wrapper').toggleClass('playing', play);
};

/*
INTERNAL FUNCTIONS
 */

/**
 * Set app [`container`]{@link module:core/view.container} target.
 *
 * The default container element is `#srt-app`, but it may be changed to perform certain actions. For example, [rendering modal content]{@link module:core/view~renderModal} temporarily changes the container target to the modal container, so that render actions only apply to the appropriate content.
 *
 * @since 3.0.0
 *
 * @param {jQuery} target - jQuery object of the desired container selection. Default: `$('#srt-app')`
 *
 * @see Used by:
 * @see [**`init`**]{@link module:core/view.init}
 * @see [**`renderModal`**]{@link module:core/view~renderModal}
 */
const setContainer = (target = $('#srt-app')) => {
  container = target;
}

/**
 * Render app-critical and conditional elements to the view `html`.
 *
 * This method serves to append any required content to the primary `index.html` file which isn't included on app load. This is done primarily to simplify the implementation and maintenance of required app content, as well as allow the inclusion of conditional elements. An example might be a "close" button in an offline build that is not required in any other mode.
 *
 * The source of the rendered content can be found in `src/view/core/partials/core-html.hbs`.
 *
 * Additionally during this method:
 * - [Language-related elements are updated]{@link module:core/view~updateLang} based on the current config lang.
 * - Settings-specific data attributes are added to app [`container`]{@link module:core/view.container}. Attributes added are those provided by the `app.schema` [attribute settings]{@link module:core/config.app_settings}. Setting data attributes are added in the form of `data-[name]="[value]"`.
 *
 * @since 3.0.0
 *
 * @see [`renderContentTo`]{@link module:core/view.renderContentTo}
 * @see [`updateLang`]{@link module:core/view~updateLang}
 * @see Used by:
 * @see [**`init`**]{@link module:core/view.init}
 *
 * @example
 * <!-- before render -->
 * <body>
 *   <main id="srt-app"></main>
 * </body>
 *
 * <!-- after render -->
 * <body>
 *   <main id="srt-app">
 *     <div class="l-app"></div>
 *     <div class="preloader"><div></div></div>
 *     <div class="u-clickCatch js-closeModal"></div>
 *   </main>
 * </body>
 */
const renderAppElements = () => {
  // render app elements
  renderContentTo(container, 'core-html', {settings: App.settings});
  // update lang-specific items
  updateLang();
};

/**
 * Add settings-specific data attributes to app [`container`]{@link module:core/view.container}. Attributes added are those provided by the `app.schema` [attribute setting]{@link module:core/config.app_settings}. Setting data attributes are added in the form of `data-[name]="[value]"`.
 *
 * @since 3.4.0
 *
 * @see Used by:
 * @see [**`postRenderViewActions`**]{@link module:core/view.postRenderViewActions}
 *
 * @example
 * <!-- before update -->
 * <body>
 *   <main id="srt-app"></main>
 * </body>
 *
 * <!-- after update -->
 * <body>
 *   <main id="srt-app" data-app_vendor="intel" data-app_id="srt-online" data-platform="iframe">
 *   </main>
 * </body>
 */
const updateAppAttributes = () => {
  // update path depth
  container.attr(`data-path-depth`, App.state.path_depth);
  // add data attributes for all app attribute settings
  for (let name of Object.keys(App.settings.app.schema)) {
    container.attr(`data-${name}`, App.state.attr.app[name]);
  }
};

/**
 * Add link to language-specific css file to HTML, if one is included for the current language. Whether the current language includes a css file is determined by the `lang_styles` [app setting]{@link module:core/config.app_settings}.
 *
 * Link element is given the id `langStyles`, and the path to the lang stylesheet is `assets/styles`.
 *
 * @since 3.2.0
 *
 * @see [`updateLangStyles`]{@link module:core/view~updateLangStyles}
 * @see Used by:
 * @see [**`?`**]{@link module:core/view}
 *
 * @example
 * // Stylesheet link added to HTML head element
 * <head>
 *   <link rel="stylesheet" href="assets/styles/en.css" id="langStyles">
 * </head>
 */
const updateLangStyles = () => {
  // remove previous
  const id = 'langStyles';
  $(`#${id}`).remove();
  // do nothing if current lang does not have a stylesheet
  if (!App.settings.lang_styles.includes(App.settings.lang)) return;
  // create new
  const link = document.createElement('link');
  link.setAttribute('rel', 'stylesheet');
  link.setAttribute('href', `${App.settings.lang_styles_location}/${App.settings.lang}.css`);
  link.setAttribute('id', id);
  document.head.appendChild(link);
};

/**
 * Add event listeners to app [`container`]{@link module:core/view.container}.
 *
 * Since internal app content is changing with each view render, event listeners are bound to the app container. Event listeners added with this function include:
 *
 * - [Navigation]{@link module:core/view.navigate} and [`navigation` analytics event]{@link module:core/analytics.event} on `.js-navButton` click.
 * - [Navigation]{@link module:core/view.navigate} to "view all" page on key combo `CTRL + SHIFT + ALT + D`.
 * - [Offsite navigation]{@link module:core/view.navigateOffsite} on `.js-offsiteLink` or `.js-continueOffsite` click.
 * - [Close modals]{@link module:core/view.closeModals} on `.js-closeModal` click or `esc` keydown.
 * - If [QRCode view setting]{@link module:core/config.QRCode} is enabled, [open `.js-qrCode` popup]{@link module:core/view.togglePopup} on `.js-qrCode-button` click.
 * - [Close popup]{@link module:core/view.togglePopup} on `.js-popupClose` click.
 * - [Show more products]{@link module:core/view~showMoreProducts} on `.js-productsShowMoreButton` click.
 * - [Scroll to element]{@link module:core/view~scrollToElement} on click, for any element with a `data-scroll-to="[selector]"` attribute.
 * - Enable module debug settings on `.js-debug` click, and [navigate]{@link module:core/view.navigate} to start. If currently in "view all" mode, [disable related settings]{@link module:core/app.toggleViewAllSettings}.
 * - [Run recommendation matrix app analysis]{@link module:core/view~runMatrix} on `.js-matrix` click.
 * - [Submit recommendation matrix results]{@link module:core/view~submitMatrixResults} for CSV download  on `.js-matrixRecommend` click.
 * - [Set up and render blueprint]{@link module:core/view~blueprintInit} on `.js-blueprint` click.
 * - Enable/disable blueprint filters on `.js-blueprint-filter` click.
 * - [Show weighted filters test popup]{@link module:core/view~filterWeights.popup} on `.js-filterWeightsTest` click.
 * - [Update filter weight test values]{@link module:core/view~filterWeights.updateTestValues} on `.js-filterWeightsTestValue` click.
 * - [Update filter weight schema setting]{@link module:core/view~filterWeights.updateWeightInputValue} on `.js-filterWeightsField input` change or keyup.
 * - [Reset filter weight inputs]{@link module:core/view~filterWeights.resetInputs} on `.js-filterWeightsReset` click.
 * - [Show product modal popup]{@link module:core/view~productModal} on `.js-productModalButton` click.
 * - [Reset attract mode timer]{@link module:core/view~resetAttractModeTimer}, if `attract_mode` [view setting]{@link module:core/config.view} is enabled.
 * - [Run resize updates]{@link module:core/view~resize} on window resize.
 * - Namespace-specific event listeners added for:
 *   - [`accordion`]{@link module:core/view.accordion.addEventListeners}
 *   - [`tabs`]{@link module:core/view.tabs.addEventListeners}
 *   - [`facet`]{@link module:core/view.facet.addEventListeners}
 * - [Form event listeners added]{@link module:core/form.addEventListeners}.
 *
 * @since 3.0.0
 *
 * @see [`Analytics.event`]{@link module:core/analytics.event}
 * @see [`navigate`]{@link module:core/view.navigate}
 * @see [`navigateOffsite`]{@link module:core/view.navigateOffsite}
 * @see [`closeModals`]{@link module:core/view.closeModals}
 * @see [`renderQRCode`]{@link module:core/view.renderQRCode}
 * @see [`togglePopup`]{@link module:core/view.togglePopup}
 * @see [`scrollToElement`]{@link module:core/view~scrollToElement}
 * @see [`showMoreProducts`]{@link module:core/view~showMoreProducts}
 * @see [`App.isViewAll`]{@link module:core/app.isViewAll}
 * @see [`App.toggleViewAllSettings`]{@link module:core/app.toggleViewAllSettings}
 * @see [`runMatrix`]{@link module:core/view~runMatrix}
 * @see [`submitMatrixResults`]{@link module:core/view~submitMatrixResults}
 * @see [`renderContentTo`]{@link module:core/view.renderContentTo}
 * @see [`blueprintInit`]{@link module:core/view~blueprintInit}
 * @see [`filterWeights.popup`]{@link module:core/view~filterWeights.popup}
 * @see [`filterWeights.updateTestValues`]{@link module:core/view~filterWeights.updateTestValues}
 * @see [`filterWeights.updateWeightInputValue`]{@link module:core/view~filterWeights.updateWeightInputValue}
 * @see [`filterWeights.resetInputs`]{@link module:core/view~filterWeights.resetInputs}
 * @see [`productModal`]{@link module:core/view~productModal}
 * @see [`resetAttractModeTimer`]{@link module:core/view~resetAttractModeTimer}
 * @see [`resize`]{@link module:core/view~resize}
 * @see [`accordion.addEventListeners`]{@link module:core/view.accordion.addEventListeners}
 * @see [`tabs.addEventListeners`]{@link module:core/view.tabs.addEventListeners}
 * @see [`facet.addEventListeners`]{@link module:core/view.facet.addEventListeners}
 * @see [`Form.addEventListeners`]{@link module:core/form.addEventListeners}
 * @see [`App.customize`]{@link module:core/app.customize}
 * @see Used by:
 * @see [**`init`**]{@link module:core/view.init}
 */
const addEventListeners = () => {
  // navigation button listener
  container.on('click', '.js-navButton', (event) => {
    event.preventDefault();
    const button = $(event.currentTarget);
    const navType = button.attr('data-nav');
    const eventType = button.attr('data-event');
    const destination = button.attr('data-destination');
    if (navType === 'restart') Analytics.event('reset_click');
    if (eventType) Analytics.event(eventType);
    navigate(navType, destination);
  });

  $(document).on('keydown', (event) => {
    if (event.altKey && event.shiftKey && event.ctrlKey && event.keyCode === keycode.d) {
      navigate('view-all');
    }
  });

  // offsite link navigation
  container.on('click', '.js-offsiteLink, .js-continueOffsite', (event) => {
    navigateOffsite(event);
  });

  if (App.settings.hatch_button.enabled) {
    // Hatch button offsite link navigation
    $(document).on('click', `#_${App.settings.hatch_button.configuration_id} a`, (event) => {
      navigateOffsite(event);
    });
  }

  // catch clicks
  container.on('click', '.js-closeModal', () => {
    closeModals();
  });

  // esc modals
  $(document).on('keyup', (event) => {
    if (event.keyCode === keycode.esc) {
      closeModals();
    }
  });

  // qr code popup button listener, if QRCode setting exists
  if (App.settings.view.QRCode.enabled) {
    container.on('click', '.js-qrCode-button', (event) => {
      let url = $(event.currentTarget).attr('data-url');
      renderQRCode(url);
      togglePopup(true, '.js-qrCode')
    });
  }

  // close popup
  container.on('click', '.js-popupClose', () => {
    togglePopup();
  });

  // scroll to element
  container.on('click', '[data-scroll-to]', (event) => {
    const element = $(event.currentTarget);
    const target = element.attr('data-scroll-to');
    scrollToElement($(target));
  });

  // show more products
  container.on('click', '.js-productsShowMoreButton', (event) => {
    showMoreProducts($(event.currentTarget));
  });

  // enable debug
  container.on('click', '.js-debug', (event) => {
    // reset view all-related settings
    if (App.isViewAll()) App.toggleViewAllSettings(false);
    const module = $(event.currentTarget).attr('data-module');
    let moduleDebug = {
      app: {debug: true},
      filter: {filter: {debug: true}},
      analytics: {analytics: {debug: true}},
    };
    App.updateSettings(moduleDebug[module]);
    navigate('restart');
  });

  // run matrix
  container.on('click', '.js-matrix', (event) => {
    runMatrix($(event.currentTarget));
  });

  // display matrix results
  container.on('click', '.js-matrixRecommend', () => {
    submitMatrixResults();
  });

  // display blueprint data
  container.on('click', '.js-blueprint', () => {
    blueprintInit();
  });

  // enable/disable blueprint filters
  container.on('click', '.js-blueprint-filter', (event) => {
    let button = $(event.currentTarget);
    let filter = button.attr('data-filter');
    let isActive = button.attr('aria-disabled') === 'false';
    let content = $('.p-blueprint__content');
    content.find(`[data-filter="${filter}"]`).attr('aria-hidden', isActive);
    button.attr('aria-disabled', isActive);
  });

  // show weighted filters test popup
  container.on('click', '.js-filterWeightsTest', (event) => {
    filterWeights.popup($(event.currentTarget));
  });

  // update filter weights test values
  container.on('click', '.js-filterWeightsTestValue', (event) => {
    filterWeights.updateTestValues($(event.currentTarget))
  });

  // update filter weight values on change
  container.on('change', '.js-filterWeightsField input', (event) => {
    filterWeights.updateWeightInputValue($(event.currentTarget));
  });

  // reset filter weight values
  container.on('click', '.js-filterWeightsReset', (event) => {
    filterWeights.resetInputs($(event.currentTarget));
  });

  // product modal event listeners
  container.on('click', '.js-productModalButton', (event) => {
    event.preventDefault();
    let button = event.currentTarget;
    const href = button.getAttribute('href');
    productModal(href);
  });

  $(document).on('keydown', (event) => {
    if (event.altKey && event.shiftKey && event.ctrlKey && event.keyCode === 84) {
      alert(Util.decode('aGVsbG8sIGhvdyBhcmUgeW91IHRvZGF5Pw'));
    }
  });

  // set attract mode, if enabled
  if (App.settings.view.attract_mode.enabled) {
    $(document).on('click', () => resetAttractModeTimer());
  }

  // resize functions
  $(window).on('resize', Util.debounce(resize, 100));

  // add namespaced event listeners
  accordion.addEventListeners();
  tabs.addEventListeners();
  facet.addEventListeners();

  // add form event listeners
  Form.addEventListeners();

  // run project-specific actions
  App.customize('addEventListeners');
}

/**
 * Manage the view's render state, and do things based on the current state. The render state in this method refers to whether a rendering-related process is currently running.
 *
 * The current render state is determined by checking for any actions currently in progress in the [`renderState`]{@link module:core/view.renderState} object, e.g. properties that have been set to `true`. Each time this method is called, an update to the state is provided, and the overall render state is checked again. If the overall render state changes after an update, then any actions that need to be run at the start or end of the rendering process are run.
 *
 * - During any state change (start/end) toggle the container's `rendering` class on/off.
 * - When rendering is complete, run [render ready actions]{@link module:core/view~renderReady}.
 *
 * @since 3.0.0
 *
 * @param {string} name - Name of the [`renderState`]{@link module:core/view.renderState} property to update.
 * @param {boolean} state - Boolean state of the `name` property. When `true`, this means the related action is currently in progress. In general, a property should be "set" (true) when an action begins, and "cleared" (false) when the action is complete.
 *
 * @see [`renderReady`]{@link module:core/view~renderReady}
 * @see Used by:
 * @see [**`init`**]{@link module:core/view.init}
 * @see [**`preRenderViewActions`**]{@link module:core/view~preRenderViewActions}
 * @see [**`postRenderViewActions`**]{@link module:core/view~postRenderViewActions}
 * @see [**`imagesLoadedInit`**]{@link module:core/view~imagesLoadedInit}
 */
const updateRenderState = (name, state) => {
  // get total render state prior to update
  const preState = Object.values(renderState).some(value => value);
  // update render state
  renderState[name] = state;
  // get new total render state
  const isRendering = Object.values(renderState).some(value => value);
  // detect state changes
  const stateChange = preState !== isRendering;
  // functionality to run on state change
  if (stateChange) {
    // things to run if rendering has started
    if (isRendering) container.addClass('rendering');
    // things to run if rendering has ended
    if (!isRendering) renderReady();
  }
};

/**
 * Render a new view with current app content, analogous to navigating to a new page. An overview of this process:
 *
 * 1. If not initial load, run any [view exit]{@link module:core/view~exitView} functionality.
 * 1. Delay render actions to show exit animations / fade in "preloader" facade element (unless initial load). Delay length is set in the `transitions.out.length` [app setting]{@link module:core/config.app_settings}. The render process includes:
 *     1. Run any [pre-render processing]{@link module:core/view~preRenderViewActions}.
 *     1. [Render current content]{@link module:core/view.renderContentTo} to render target element `.l-app`.
 *     1. Run any [post-render processing]{@link module:core/view~postRenderViewActions}.
 *     1. Set app setting `initial_load` to `false` (if not already).
 *
 * Note that this process should not be used to render modal content - that is handled by [`renderModal`]{@link module:core/view~renderModal}.
 *
 * @since 3.0.0
 *
 * @see [`exitView`]{@link module:core/view~exitView}
 * @see [`preRenderViewActions`]{@link module:core/view~preRenderViewActions}
 * @see [`renderContentTo`]{@link module:core/view.renderContentTo}
 * @see [`postRenderViewActions`]{@link module:core/view~postRenderViewActions}
 * @see Used by:
 * @see [**`navigate`**]{@link module:core/view.init}
 */
const renderView = (modal = false) => {
  // if modal, render it instead of main view
  if (modal) {
    App.state.direction === 'forward' ? renderModal() : closeContentModal();
    return;
  }
  // view exit setup
  if (!App.settings.initial_load) exitView();
  // define delay length
  let delay = App.settings.initial_load ? 0 : App.settings.transitions.out.length;
  // define actions that need to be delayed for exit animations
  let delayedActions = () => {
    // pre-render setup
    preRenderViewActions();
    // render template
    renderContentTo(container.children('.l-app'));
    // post-render setup
    postRenderViewActions();
    // Set initial load to false
    if (App.settings.initial_load) App.updateSettings({initial_load: false});
  };
  // delay actions
  setTimeout(delayedActions, delay);
}

/**
 * Render current content to modal container, and activate modal display. Enable modal side journeys with the `modal_side_journey` [setting]{@link module:core/config.app_settings}.
 *
 * This function will activate modal container element `.js-modalContainer`, and insert destination content into the modal content container `.js-modalContent`.
 *
 * An overview of this process:
 *
 * 1. [Set container]{@link module:core/view~setContainer} to `.js-modalContainer`.
 * 2. Run any [pre-render processing]{@link module:core/view~preRenderViewActions}.
 * 3. [Render current content]{@link module:core/view.renderContentTo} to render target element `.js-modalContent`.
 * 4. [Activate modal]{@link module:core/view.togglePopup} and [scroll to top]{@link module:core/view.scrollToTop} of page.
 * 5. Finally, several actions to finalize the modal content are delayed until after the modal opening animation are complete. This avoids issues with element focus placed outside of the visible window, if the modal content is hidden offscreen. Delayed actions include:
 *
 *     - Run any [post-render processing]{@link module:core/view~postRenderViewActions}.
 *     - [Trap tab navigation focus]{@link module:core/view.toggleTrapFocus} inside modal container.
 *     - [Reset container]{@link module:core/view~setContainer} to default container.
 *
 * 6. Timeout function for delayed actions is created and set to `App.settings.modalTimeout` so it can be canceled later if necessary. Default delay time is 500ms, but this value can be changed with the `modal_animation_delay` [setting]{@link module:core/config.app_settings}.
 *
 * @since 3.0.0
 *
 * @see [`setContainer`]{@link module:core/view~setContainer}
 * @see [`preRenderViewActions`]{@link module:core/view~preRenderViewActions}
 * @see [`renderContentTo`]{@link module:core/view.renderContentTo}
 * @see [`togglePopup`]{@link module:core/view.togglePopup}
 * @see [`scrollToTop`]{@link module:core/view.scrollToTop}
 * @see [`postRenderViewActions`]{@link module:core/view~postRenderViewActions}
 * @see [`toggleTrapFocus`]{@link module:core/view.toggleTrapFocus}
 * @see Used by:
 * @see [**`navigate`**]{@link module:core/view.navigate}
 */
const renderModal = () => {
  // define modal container
  const modalContainer = $('.js-modalContainer');
  const modalContentTarget = $('.js-modalContent');

  // update view container definition so we are only running view updates on modal
  setContainer(modalContainer);
  // pre-render setup
  preRenderViewActions(App.current);
  // render modal content
  renderContentTo(modalContentTarget);
  // activate modal
  togglePopup(true, modalContainer);
  scrollToTop();

  // define actions that need to be delayed while modal animates
  let delayedActions = () => {
    postRenderViewActions();
    toggleTrapFocus(modalContainer, true);
    // reset container
    setContainer();
  };

  config.modalTimeout = setTimeout(delayedActions, App.settings.modal_animation_delay);
}

/**
 * Close any open content modals.
 *
 * An overview of this process:
 *
 * 1. Clear modal timeout function, in case modal is closed before it has finished rendering.
 * 2. If modal container does not have an `active` class, the function stops here.
 * 3. [Trap focus]{@link module:core/view.toggleTrapFocus} is removed.
 * 4. [Modal is deactivated]{@link module:core/view.togglePopup} and modal content is removed from content container.
 * 5. Page focus is reset to top of page.
 * 6. Deep link is reset back to original page (if in dev mode).
 *
 * @since 3.0.0
 *
 * @see [`toggleTrapFocus`]{@link module:core/view.toggleTrapFocus}
 * @see [`togglePopup`]{@link module:core/view.togglePopup}
 * @see [`resetPageFocus`]{@link module:core/view.resetPageFocus}
 * @see [`App.setDeepLink`]{@link module:core/app.updateHistory}
 * @see Used by:
 * @see [**`navigate`**]{@link module:core/view.navigate}
 * @see [**`closeModals`**]{@link module:core/view.closeModals}
 */
const closeContentModal = () => {
  // define modal container
  const modalContainer = $('.js-modalContainer');
  const modalContentTarget = $('.js-modalContent');

  // clear any active modal timeouts
  clearTimeout(config.modalTimeout);
  // do nothing if modal container is not active
  if (!modalContainer.hasClass('active')) return;
  // remove focus trap
  toggleTrapFocus(modalContainer);
  // close modal and clean out content
  togglePopup(false, modalContainer);
  modalContentTarget.html('');
  // reset focus
  resetPageFocus();
  // reset deep link
  App.updateHistory();
}

/**
 * Trigger view exit actions.
 *
 * - Sets up [progressive delay]{@link module:core/view~progressiveDelay} for exit animations.
 * - Adds class `exitView` to current container, which can be used to handle exit animations in styles.
 * - Add loading notification text for screen readers. **Must have `aria_loading` label set for this to be announced.**
 *
 * @since 3.0.0
 *
 * @see [`progressiveDelay`]{@link module:core/view~progressiveDelay}
 * @see [`App.customize`]{@link module:core/app.customize}
 * @see Used by:
 * @see [**`renderView`**]{@link module:core/view~renderView}
 */
const exitView = () => {
  // set up progressive delay elements
  progressiveDelay(false);
  // add exit class
  container.addClass('exitView');
  // set loader to busy
  container.find('.js-loading').html(App.settings.labels['aria_loading']);
  // run project-specific actions
  App.customize('exitView');
};

/**
 * Prepare view for rendering.
 *
 * - Removes class `exitView` from current container.
 * - [Update render state]{@link module:core/view~updateRenderState} to set `rendering` to `true`.
 * - Disables any active [click catch]{@link module:core/view.toggleClickCatch} elements.
 * - Runs any `preRenderViewActions` [project customizations]{@link module:core/app.customize}.
 *
 * @since 3.0.0
 *
 * @see [`updateRenderState`]{@link module:core/view~updateRenderState}
 * @see [`toggleClickCatch`]{@link module:core/view.toggleClickCatch}
 * @see [`App.customize`]{@link module:core/app.customize}
 * @see Used by:
 * @see [**`renderView`**]{@link module:core/view~renderView}
 * @see [**`renderModal`**]{@link module:core/view~renderModal}
 */
const preRenderViewActions = () => {
  // remove exit class, add rendering class
  container.removeClass('exitView');
  // update rendering state
  updateRenderState('rendering', true);
  // remove click catch
  toggleClickCatch();
  // run project customization
  App.customize('preRenderViewActions');
}

/**
 * Actions to take after template is rendered. A full list of actions can be found in the "see" section.
 *
 * Note:
 * - [`postNavRenderActions`]{@link module:core/view~postNavRenderActions} is only run if `nav` param is `true`.
 *
 * @param {boolean} [nav] - When enabled, [`postNavRenderActions`]{@link module:core/view~postNavRenderActions} are run.
 *
 * @since 3.0.0
 *
 * @see [`postNavRenderActions`]{@link module:core/view~postNavRenderActions}
 * @see [`imagesLoadedInit`]{@link module:core/view~imagesLoadedInit}
 * @see [`hatchButtonInit`]{@link module:core/view~hatchButtonInit}
 * @see [`Form.inputInit`]{@link module:core/form.inputInit}
 * @see [`removeUnusedElements`]{@link module:core/view~removeUnusedElements}
 * @see [`accordion.init`]{@link module:core/view.accordion.init}
 * @see [`App.customize`]{@link module:core/app.customize}
 * @see [`updateRenderState`]{@link module:core/view~updateRenderState}
 * @see Used by:
 * @see [**`renderView`**]{@link module:core/view~renderView}
 * @see [**`renderModal`**]{@link module:core/view~renderModal}
 * @see [**`showMoreProducts`**]{@link module:core/view~showMoreProducts}
 */
const postRenderViewActions = (nav = true) => {
  // initialize inputs
  Form.inputInit();
  // remove any unneeded elements
  removeUnusedElements();
  if (nav) postNavRenderActions();
  // are images loaded?
  imagesLoadedInit();
  // initialize hatch button
  hatchButtonInit();
  // initialize accordions
  accordion.init();
  // run project-specific actions
  App.customize('postRenderViewActions');
  // update render state
  updateRenderState('rendering', false);
};

/**
 Actions to take after template is rendered when navigation has taken place. A full list of actions can be found in the "see" section.

 Note:
 - [`App.setAttributes`]{@link module:core/app.setAttributes} is only run if app navigation direction is `back`.
 - [`scrollToTop`]{@link module:core/view.scrollToTop} and [`resetPageFocus`]{@link module:core/view.resetPageFocus} are only run if this is not the app's initial load/render.

 @see [`resetConfigState`]{@link module:core/view~resetConfigState}
 @see [`App.setAttributes`]{@link module:core/app.setAttributes}
 @see [`App.updateHistory`]{@link module:core/app.updateHistory}
 @see [`setActiveSelections`]{@link module:core/view~setActiveSelections}
 @see [`updateState`]{@link module:core/view.updateState}
 @see [`updateAppAttributes`]{@link module:core/view~updateAppAttributes}
 @see [`resetAttractModeTimer`]{@link module:core/view~resetAttractModeTimer}
 @see [`scrollToTop`]{@link module:core/view.scrollToTop}
 @see [`resetPageFocus`]{@link module:core/view.resetPageFocus}
 @see Used by:
 @see [**`postRenderViewActions`**]{@link module:core/view~postRenderViewActions}
 */
const postNavRenderActions = () => {
  // reset config state
  resetConfigState();
  // if navigating back, temporarily reset attributes to view initial state
  if (App.state.direction === 'back') App.setAttributes();
  // deep link only used for dev
  App.updateHistory();
  // activate selections
  setActiveSelections();
  // update app state
  updateState();
  // update app attributes
  updateAppAttributes();
  // set attract mode
  resetAttractModeTimer();
  // scroll to top of app and reset focus, if not first load
  if (!App.settings.initial_load) {
    scrollToTop();
    resetPageFocus();
  }
};

/**
 * Reset config options which hold data for each view.
 *
 * @since 3.4.0
 *
 * @see Used by:
 * @see [**`postRenderViewActions`**]{@link module:core/view~postRenderViewActions}
 */
const resetConfigState = () => {
  // reset active options
  config.activeOptions = [];
  // reset failed images
  config.failedImages = {};
};

/**
 * Actions to run on view render init completion.
 *
 * *Note that functionality should only be run here if the actions **depend on knowing the timing of the render state completion**, e.g. progressively fading in items when the view becomes visible. Otherwise, use [pre]{@link module:core/view~preRenderViewActions}- or [post]{@link module:core/view~postRenderViewActions}-render view action methods.*
 *
 * - [Render image build info]{@link module:core/view~addImageBuildInfo}
 * - [Run progressive delay animations]{@link module:core/view~progressiveDelay}.
 * - [Reset page focus]{@link module:core/view.resetPageFocus}, if not initial load.
 * - Clear loading state for screen readers.
 *
 * @since 3.3.1
 *
 * @see [`addImageBuildInfo`]{@link module:core/view~addImageBuildInfo}
 * @see [`progressiveDelay`]{@link module:core/view~progressiveDelay}
 * @see [`resetPageFocus`]{@link module:core/view.resetPageFocus}
 * @see [`App.customize`]{@link module:core/app.customize}
 * @see Used by:
 * @see [**`updateRenderState`**]{@link module:core/view~updateRenderState}
 */
const renderReady = () => {
  // add build
  addImageBuildInfo();
  // run progressive delay
  progressiveDelay();
  // clear loader busy state
  container.find('.js-loading').html('');
  // remove rendering class
  container.removeClass('rendering');
  // run project-specific actions
  App.customize('renderReady');
  // reset container
  setContainer();
};

/**
 * Run [imagesLoaded]{@link https://github.com/desandro/imagesloaded} setup for the current [`container`]{@link module:core/view.container}.
 *
 * If Intel image service API is enabled as a fallback, or a [product fallback image]{@link module:core/config.fallback_img_src} is provided through settings config or JSON, any broken images will load the fallback.
 *
 * If any image `src` updates are made due to failures, this method is called again so it can check on the state of replacement sources. Additionally, if the current view is a view all page, failed product image urls will be [collected]{@link module:core/view~viewAllImageDebug}. The [render state]{@link module:core/view~updateRenderState} is updated with a unique state name each time the method is run, only clearing once all images have successfully resolved.
 *
 * @since 3.0.0
 *
 * @see [`updateRenderState`]{@link module:core/view~updateRenderState}
 * @see [`viewAllImageDebug`]{@link module:core/view~viewAllImageDebug}
 * @see Used by:
 * @see [**`postRenderViewActions`**]{@link module:core/view~postRenderViewActions}
 */
const imagesLoadedInit = (element = container) => {
  // get timestamp for unique imagesLoading render state
  let timeStamp = Date.now();
  // update rendering state
  updateRenderState(`imagesLoading-${timeStamp}`, true);
  // make a copy of current container to use as a target.
  // since image processing is run async, the original container element may have changed by the time it is finished.
  let target = $(element);
  let imgLoad = imagesLoaded(target[0]);
  // will be set to true if any src replacements occur
  let srcUpdates = false;

  // check for broken image fallback urls
  imgLoad.on('progress', (instance, image) => {
    // if image fails
    if (!image.isLoaded) {
      // collect image info for view all debug
      viewAllImageDebug(image);
      // determine if a provided fallback src is active
      let isFallbackActive = image.img.dataset.fallbackSrc && image.img.src.includes(image.img.dataset.fallbackSrc);
      // if conditions exist to try API image fallback, update image src
      let api = App.settings.api_images;
      if (api.enabled && api.fallback_only && !Util.isImageAPIURL(image.img.src) && !isFallbackActive) {
        srcUpdates = true;
        image.img.src = Util.getImageAPIURL(image.img.src);
      }
      // add local image fallback, if it exists and is not already active
      else if (image.img.dataset.fallbackSrc && !isFallbackActive) {
        srcUpdates = true;
        image.img.src = image.img.dataset.fallbackSrc;
      }
    }
  });
  // after each run, resolve the current run
  imgLoad.on('always', () => {
    // update rendering state
    updateRenderState(`imagesLoading-${timeStamp}`, false);
    // polyfill object fit
    objectFitPolyfill();
  });
  // run again on fail, if sources were updated
  imgLoad.on('fail', () => {
    if (srcUpdates) imagesLoadedInit();
  });
}

/**
 * Collect debugging info for failed images, to be displayed on the view all page.
 *
 * If current page is a [view all]{@link module:core/app.isViewAll} page, any failed image URLs are added to the `[config]{@link module:core/view.config}.failedImages` object, to eventually be [reported]{@link module:core/view~addImageBuildInfo} if necessary.
 *
 * @since 3.4.1
 *
 * @param {object} image - `imagesLoaded` image object.
 *
 * @see Used by:
 * @see [**`imagesLoadedInit`**]{@link module:core/view~imagesLoadedInit}
 */
const viewAllImageDebug = (image) => {
  if (!App.isViewAll()) return;
  // attempt to get product title for failed images
  let product = $(image.img).closest('[data-product]');
  let productID = product.attr('data-product-id');
  let productName = product.find('[data-product-title]').text();
  let productUniqueName = `[${productID}] ${productName}`;
  // create array for product image urls, if one does not exist
  if (!Array.isArray(config.failedImages[productUniqueName])) config.failedImages[productUniqueName] = [];
  // push image source to product array
  config.failedImages[productUniqueName].push(image.img.src);
};

/**
 * If any [images fail to load]{@link module:core/view~imagesLoadedInit} in a [view all]{@link module:core/app.isViewAll} page, report them in build info and re-render the build info content.
 *
 * @since 3.4.0
 *
 * @see [`App.isViewAll`]{@link module:core/app.isViewAll}
 * @see [`App.flagBuildIssue`]{@link module:core/app.flagBuildIssue}
 * @see [`renderContentTo`]{@link module:core/view.renderContentTo}
 * @see Used by:
 * @see [**`renderReady`**]{@link module:core/view~renderReady}
 */
const addImageBuildInfo = () => {
  // if no failed images, or view is not view all, do nothing
  if (!Object.keys(config.failedImages).length || !App.isViewAll()) return;
  // report failed images
  App.current.settings.imageInfo = [];
  for (let [name, label] of Object.entries(config.failedImages)) App.current.settings.imageInfo.push({ name, label });
  // replace rendered build info
  let buildInfo = container.find('[data-build-info]');
  let parent = buildInfo.parent();
  buildInfo.remove();
  renderContentTo(parent, 'core-build-info', App.current, true);
};

/**
 * Remove any elements that aren't needed in the current view.
 *
 * Facet inputs that are unavailable in the current inventory are removed permanently with a destructive [facet filter]{@link module:core/view.facet.filter}. Additionally, any empty parent fields or accordion sections left over (due to removed inputs, either during faceting or [content preparation]{@link module:core/app~transformContent}) are also removed.
 *
 * @since 3.0.0
 *
 * @see [`Form.removeEmptyFields`]{@link module:core/form.removeEmptyFields}
 * @see [`accordion.removeEmpty`]{@link module:core/view.accordion.removeEmpty}
 * @see [`App.customize`]{@link module:core/app.customize}
 * @see Used by:
 * @see [**`postRenderViewActions`**]{@link module:core/view~postRenderViewActions}
 *
 * @example
 * <!-- assume inventory contains no i3 products or touchscreen devices -->
 *
 * <!-- before removal -->
 * <div class="field">
 *   <input type="radio">Intel i3 processor</button>
 *   <input type="radio">Intel i5 processor</button>
 *   <input type="radio">Intel i7 processor</button>
 * </div>
 *
 * <div class="field">
 *   <input type="checkbox">Touchscreen</button>
 * </div>
 *
 * <!-- after removal -->
 * <div class="field">
 *   <input type="radio">Intel i5 processor</button>
 *   <input type="radio">Intel i7 processor</button>
 * </div>
 */
const removeUnusedElements = () => {
  // if faceting is enabled in current view content settings, run facet filter once first (destructively)
  if (App.current.settings.facet.enabled) facet.filter(true);
  // remove any fields that are empty as a result of removing inputs
  Form.removeEmptyFields();
  // remove accordion sections that contain no inputs (if necessary)
  accordion.removeEmpty();
  // run project-specific actions
  App.customize('removeUnusedElements');
}

/**
 * Applies any existing selections to a view (e.g. when navigating backwards).
 *
 * ### Inputs
 *
 * Any inputs that were previously selected will be re-applied to the view when traveling backwards, unless the `auto_continue` [page setting]{@link module:core/config.page_settings} is enabled.
 *
 * Inputs are enabled with [`inputActions`]{@link module:core/view.inputActions}, using the list of active input IDs saved during [`setAttributes`]{@link module:core/view.updateNodeState}.
 *
 * ### Tabs
 *
 * Previous [tab]{@link module:core/view.tabs} states are applied when traveling backwards, so that visual state is consistent with when the view was last left.
 *
 * ### Apply past selections
 * See [`applyPastSelections`]{@link module:core/view~applyPastSelections}.
 *
 * @since 3.0.0
 *
 * @see [`App.currentNodeSetting`]{@link module:core/app.currentNodeSetting}
 * @see [`inputActions`]{@link module:core/view.inputActions}
 * @see [`getInputByID`]{@link module:core/view.getInputByID}
 * @see [`tabs.open`]{@link module:core/view.tabs.open}
 * @see [`setActiveSelections`]{@link module:core/view~setActiveSelections}
 * @see [`App.customize`]{@link module:core/app.customize}
 * @see Used by:
 * @see [**`postRenderViewActions`**]{@link module:core/view~postRenderViewActions}
 *
 * @example
 * <!-- apply previous input states when traveling back -->
 *
 * <!-- node stored selections -->
 * <!-- selections = ['input_1', 'input_4']; -->
 *
 * <!-- input states after running method -->
 * <div data-view>
 *   <button id="input_1" class="input active">Organizing photos online</button>
 *   <button id="input_2" class="input">Editing images</button>
 *   <button id="input_3" class="input">Video editing</button>
 *   <button id="input_4" class="input active">Gaming</button>
 * </div>
 *
 * @example
 * <!-- apply previous tab states when traveling back -->
 *
 * <!-- node stored tab indices for two tab groups -->
 * <!-- tabs = [1, 0] -->
 *
 * <!-- tab states after running method -->
 * <div id="tab_group_1">
 *   <div id="tab_group_1_tab_1">...</div>
 *   <div id="tab_group_1_tab_2 active">...</div>
 * </div>
 * <div id="tab_group_2">
 *   <div id="tab_group_2_tab_1 active">...</div>
 *   <div id="tab_group_2_tab_2">...</div>
 * </div>
 */
const setActiveSelections = () => {
  const isAutoContinue = App.currentNodeSetting('auto_continue');

  // set any active selections saved from previous steps in path
  if (!isAutoContinue) for (let id of App.state.current.selections) inputActions(getInputByID(id), false, true);
  // run post input actions
  updateState();

  // set any active tabs
  if (App.state.current.tabs.length) {
    // activate any tabs in tab groups matching tab indices
    App.state.current.tabs.forEach((activeIndex, tagGroupIndex) => {
      const currentTabGroup = $('.js-tabs').eq(tagGroupIndex);
      const activeTab = currentTabGroup.find('.js-tab').eq(activeIndex);
      tabs.open(activeTab, true);
    });
  }

  // apply past selections, if applicable
  applyPastSelections();

  // run customization
  App.customize('setActiveSelections');
}

/**
 * Apply selections made earlier in user path to inputs in the current view, if the `apply_past_selections` [page setting]{@link module:core/config.page_settings} is enabled, and the most recent navigation direction is not backwards.
 *
 * Specifically, any attribute type with the `applyActive` property enabled will be checked against the current view's selection options, and selections with attributes matching those found in the path will be activated. If any text fields with associated attributes are present, attribute values will be inserted as the text input value.
 *
 * This is useful for things like a "specs" screen, where the user can modify and fine-tune their recommendation after going through the main app path. Note that it may be necessary to use the `anchor_path_attributes` [page setting]{@link module:core/config.page_settings} in conjunction with `apply_past_selections` to prevent past selections from affecting a recommendation (e.g. in the case of a specs screen).
 *
 * @since 3.2.3
 *
 * @see [`App.currentNodeSetting`]{@link module:core/app.currentNodeSetting}
 * @see [`inputAttributes`]{@link module:core/view.inputAttributes}
 * @see [`App.attributesMatch`]{@link module:core/app.attributesMatch}
 * @see [`inputActions`]{@link module:core/view.inputActions}
 * @see Used by:
 * @see [**`setActiveSelections`**]{@link module:core/view~setActiveSelections}
 *
 * @example
 * <!-- apply path selections to inputs when traveling forward -->
 *
 * <!-- path attributes from previous selections -->
 * <!-- attr = {
 *   form_factor: "notebook",
 *   processor: "i7",
 * } -->
 *
 * <!-- inputs after running method -->
 * <h2>Form factor</h2>
 * <button id="input_1" class="input active">Notebook</button>
 * <button id="input_2" class="input">Desktop</button>
 *
 * <h2>Processor</h2>
 * <button id="input_3" class="input">Intel i3</button>
 * <button id="input_4" class="input">Intel i5</button>
 * <button id="input_5" class="input active">Intel i7</button>
 */
const applyPastSelections = () => {
  // if apply_past_selections is not enabled, or the navigation direction is back, do nothing
  if (!App.currentNodeSetting('apply_past_selections') || App.state.direction === 'back') return;
  // get attribute definitions which are valid to apply
  const applyActiveAttrDef = Util.filterObjectArray(App.attributes, 'applyActive', true);
  // get all view inputs, as an array
  let inputs = container.find('.input').toArray();
  // determine if this is also an attribute anchor view
  const isAnchor = App.currentNodeSetting('anchor_path_attributes');
  // loop through inputs
  for (let element of inputs) {
    const input = $(element);
    // is input text field
    const isText = input.is('.js-textInput');
    // is input file field
    const isFile = input.is('.js-fileInput');
    // check if input attributes match current attributes
    let attributesMatch = inputAttributeMatch(input, applyActiveAttrDef, isAnchor);
    // if not activating, continue
    if (!attributesMatch.length) continue;
    // process text and file labels
    if (isText || isFile) {
      let label = attributesMatch.reduce((label, attribute) => attribute.label || label, '');
      // if text field, update labels, if available
      if (isText && label) input.val(label);
      // if file field, add label as fallback image source
      if (isFile && label) input.attr('data-fallback-src', label);
    }
    // activate input passively
    inputActions(input, false, true);
    // update app state
    updateState();
  }
};

/**
 * Setup progressive fade-in for selected view elements.
 *
 * Elements with the class `fadeIn` are animated in on view load. Each item within app container is progressively delayed compared to the previous item. This is done by adding a temporary inline transition delay that grows incrementally with each item index. The size of the increment is determined by dividing the number of elements by a set time, which allows the animation process to always complete in the same amount of time regardless of the number of items (prevents long fade in delays with lots of items).
 *
 * Inline transition styles are removed after animation is complete, to prevent affecting elements with existing transition styles.
 *
 * @since 3.0.0
 *
 * @param {boolean} fadeIn - Whether fade direction is "in" or "out". Default: `true`.
 * @param {boolean|jQuery} forceElements - If a set of jQuery elements is provided, progressive delay will be forced on all items with the `fadeIn` class, even if they are not currently visible.
 *
 * @see Used by:
 * @see [**`postRenderViewActions`**]{@link module:core/view~postRenderViewActions}
 *
 * @example
 * <!-- elements with temporary transition styles -->
 * <div class="field">
 *   <button class="input fadeIn" style="transition-delay: 0s; transition-duration: 0.5s;">Organizing photos online</button>
 *   <button class="input fadeIn" style="transition-delay: 0.05s; transition-duration: 0.5s;">Editing images</button>
 *   <button class="input fadeIn" style="transition-delay: 0.1s; transition-duration: 0.5s;">Video editing</button>
 * </div>
 */
const progressiveDelay = (fadeIn = true, forceElements = false) => {
  const targets = forceElements || container;
  const excludeItems = '.hidden, .js-accordionContent';
  const dir = fadeIn ? 'in' : 'out';
  // timing variables
  let transitionDuration = App.settings.transitions[dir].length;
  let totalProgressive = App.settings.transitions[dir].progressive / 1000; // time over which to distribute progressive delay gaps
  let animationDuration = (transitionDuration - totalProgressive) / 1000; // css animation length
  let reverse = App.settings.transitions[dir].reverse; //
  // add transition delay to each stack item
  targets.each((i, element) => {
    const target = $(element);
    // add animation delay for each element with the .fadeIn class
    let elements = forceElements ? target.find('.fadeIn') : target.find('.fadeIn').filter(':visible').filter((i, element) => $(element).css('visibility') !== 'hidden').not(excludeItems);
    // reverse items if needed
    if (reverse) elements = $(elements.get().reverse());
    // set item delay based on number of in stack
    // if no progressive delay is set, use zero
    let itemDelay = totalProgressive ? totalProgressive / elements.length : 0;
    // loop through each element
    elements.each((i, element) => {
      const item = $(element);
      let val = (i * itemDelay);
      item.css({
        'transition-delay': val + 's',
        'transition-duration': `${animationDuration}s`,
      });
      // remove inline styles when animation is safely finished
      setTimeout(() => {
        item.removeAttr('style');
      }, transitionDuration);
    });
  });
};

/**
 * Determine valid destination(s) for the current view.
 *
 * Destinations, or the IDs of views to visit in the future, may be defined at the [page]{@link module:core/config.page} level or the individual [`options`]{@link module:core/config.options} level. Destinations defined on selected options will take precedence over view destinations.
 *
 * In addition, if multiple options are selected, either all destinations will be visited in order, or the highest priority destination will be used (see [options object]{@link module:core/config.options} for more info on destination priorities).
 *
 * Returns an array of destination strings.
 *
 * @since 3.0.0
 *
 * @return {array}
 *
 * @see Used by:
 * @see [**`updateState`**]{@link module:core/view.updateState}
 */
const getDestinations = () => {
  // filter active options to only those with destination properties
  const options = config.activeOptions.filter(element => !!element.destination);

  // if there are no action destination options, return view destination, or if there isn't one, an empty array
  if (!options.length) return App.current.destination ? [App.current.destination] : [];

  // if there are destination options, determine which to use
  if (options.length) {
    // get options with destination priorities
    let priorities = options.filter(element => !!element.destination_priority);

    // if there are no priority options, return all active destinations
    if (!priorities.length) return options.map(option => option.destination);

    // if more than one priority option, reduce to highest priority option
    if (priorities.length > 0) {
      // reducer checks the option priorities against each other to find the lowest value.
      // this will be the highest priority item
      let option = priorities.reduce((priority, option) => priority.destination_priority < option.destination_priority ? priority : option);
      // return option destination
      return [option.destination];
    }
  }
};

/**
 * Reset attract mode timer.
 *
 * - Clears any previously set timer, and [disable attract mode]{@link module:core/view~toggleAttractMode}.
 * - If on app `start` view, set a new timer, which [enables attract mode]{@link module:core/view~toggleAttractMode} when complete.
 *
 * @since 3.4.5
 *
 * @param {number} [delay] - Time in seconds until attract mode is enabled. Default: `App.settings.view.attract_mode.delay`
 *
 * @see [`toggleAttractMode`]{@link module:core/view~toggleAttractMode}
 * @see Used by:
 * @see [**`postRenderViewActions`**]{@link module:core/view.postRenderViewActions}
 * @see [**`addEventListeners`**]{@link module:core/view~addEventListeners}
 * @see [**`toggleAttractMode`**]{@link module:core/view~toggleAttractMode}
 */
const resetAttractModeTimer = (delay = App.settings.view.attract_mode.delay) => {
  // if attract mode is not yet defined or not enabled, do nothing
  if (!App.settings.view.attract_mode.enabled) return;
  // clear previous timeouts
  clearTimeout(config.attract_mode.on_timer);
  clearTimeout(config.attract_mode.off_timer);
  // if we're not on the start view, stop here
  if (App.state.current.location !== App.settings.start) return;
  // disable attract mode
  toggleAttractMode(false);
  // turn on attract mode after delay
  config.attract_mode.on_timer = setTimeout(() => {
    toggleAttractMode();
  }, delay * 1000);
}

/**
 * Enable/disable attract mode. Adds or removes class `attractMode` on view [`container`]{@link module:core/view.container} based on the `enable` settings.
 *
 * @since 3.4.5
 *
 * @param {boolean} enable - Whether attract mode should be enabled or disabled. Default: `true`
 *
 * @see [`resetAttractModeTimer`]{@link module:core/view~resetAttractModeTimer}
 * @see Used by:
 * @see [**`resetAttractModeTimer`**]{@link module:core/view~resetAttractModeTimer}
 */
const toggleAttractMode = (enable = true) => {
  // if not enabled, do nothing
  if (!App.settings.view.attract_mode.enabled) return;
  // toggle attract mode class
  container.toggleClass('attractMode', enable);
  // allow project customizations
  App.customize('toggleAttractMode', enable);
  // if disabling, stop here
  if (!enable) return;
  // turn off attract mode after delay
  config.attract_mode.off_timer = setTimeout(() => resetAttractModeTimer(App.settings.view.attract_mode.off_length), App.settings.view.attract_mode.on_length * 1000);
};

/**
 * Scroll to a provided element.
 *
 * **Important:** this method scrolls the current `container` rather than the typical `html` or `body` elements, and gets the target element's position relative to the nearest positioned element (the offset parent). As such, when using this method, the `container` should be treated as the app "window", which requires an element with `position: relative` and auto `height` to act as the content wrapper.
 *
 * @since 3.1.1
 *
 * @param {jQuery|HTMLElement} element - Element to scroll to. If multiple elements are provided, the first one is used.
 *
 * @param {number} offset - Number of pixels to offset scroll target. Negative numbers will scroll to a point above the start of the element, and positive numbers will scroll to a point below the start of the element. Default: `0`
 *
 * @param {number} length - Scroll animation length in ms. Default: `500`
 *
 * @see Used by:
 * @see [**`addEventListeners`**]{@link module:core/view~addEventListeners}
 */
const scrollToElement = (element, offset = 0, length = 500) => {
  const target = $(element).first();
  // if target does not exist, stop here
  if (!target.length) {
    App.debug(4, 'Target element not found.');
    return;
  }
  let targetPositionTop = target.position().top;
  // animate scroll to element
  container.animate({
    scrollTop: targetPositionTop + offset,
  }, length);
};

/**
 * If [direct-to-retailer]{@link module:core/config.direct_to_retailer} (DTR) functionality is enabled, and the link is currently present in the view, set the correct link value on it. Link value is determined by [comparing a link object's `condition`]{@link module:core/app.attributesMatch} against the active [primary attribute]{@link module:core/app~getPrimaryAttribute} values.
 *
 * In order for DTR functionality to work, the project must have a valid DTR link shown when recommendations are ready. Determining when the DTR link should be shown is handled by [`App.setDTRContinue`]{@link module:core/app~setDTRContinue}. DTR link elements must meet the following requirements:
 * - Element must be an `<a></a>` tag (href will be set dynamically)
 * - Must include the class `.js-offsiteLink`
 * - Must include attribute `data-nav="direct-to-retailer"`
 *
 * A functional DTR link is included in the core nav partial (`core-nav.hbs`), but since project navigation is commonly customized, it should be verified that a valid DTR link is present in project navigation partial.
 *
 * @since 3.0.0
 *
 * @see [`App.attributesMatch`]{@link module:core/app.attributesMatch}
 * @see Used by:
 * @see [**`setNavigationState`**]{@link module:core/view.setNavigationState}
 *
 * @example
 * <!-- example DTR link, displayed conditionally -->
 * {{#if @root.settings.direct_to_retailer.continue}}
 *   <!-- DTR link -->
 *   <a href="" class='js-offsiteLink' data-nav='direct-to-retailer'>View retailer recommendations</a>
 * {{else}}
 *   <!-- default continue -->
 *   <a class='js-navButton' data-nav='forward'>Next</a>
 * {{/if}}
 *
 */
const setDTRLink = () => {
  // if not enabled, do nothing
  if (!App.settings.direct_to_retailer.enabled) return;
  // get DTR anchor, selected primary attributes, and link list
  const anchor = container.find('a.js-offsiteLink[data-nav="direct-to-retailer"]');
  const attrType = App.settings.primary_attribute.type;
  const attr = App.state.attr[attrType];
  const links = App.settings.direct_to_retailer.links;
  // if no button or links are found, do nothing
  if (!anchor.length || !links) return;
  // reduce links to last match
  let href = links.reduce((link, linkObj) => App.attributesMatch(linkObj.condition, attr, {type: attrType}) ? linkObj['link'] : link, '');
  // add href content, and if no href, add class so an alert is shown instead
  anchor.attr('href', href).toggleClass('js-noRetailerLink', !href);
};

/**
 * Add the Hatch button script to view, when `hatch_button` [setting]{@link module:core/config.app_settings} is enabled.
 *
 * @since 3.4.0
 *
 * @see Used by:
 * @see [**`init`**]{@link module:core/view.init}
 */
const hatchScriptInit = () => {
  // do nothing if hatch button is not enabled
  if (!App.settings.hatch_button.enabled) return;
  let s = 'script';
  let id = 'hatch-buy-now-request-';
  if (document.getElementById(id + s)) return;
  let js, t = document.getElementsByTagName(s)[0];
  js = document.createElement(s);
  js.id = id + s;
  js.src = App.settings.hatch_button.script;
  t.parentNode.insertBefore(js, t);
};

/**
 * Initialize Hatch button widget(s), when the following conditions are met:
 *
 * - The `hatch_button` [setting]{@link module:core/config.app_settings} is enabled
 * - Current view content includes products
 * - `HatchRequest` is defined by [included Hatch script]{@link module:core/view~hatchScriptInit}
 *
 * @since 3.4.0
 *
 * @see Used by:
 * @see [**`postRenderViewActions`**]{@link module:core/view~postRenderViewActions}
 */
const hatchButtonInit = () => {
  // do nothing if hatch_button is not enabled, there are no products, or the hatch object is not defined
  if (!App.settings.hatch_button.enabled || !App.current.products || typeof HatchRequest === undefined) return;
  // run init function
  HatchRequest.init();
  // clear any previous timeouts
  if (config.hatch_timeout) clearTimeout(config.hatch_timeout);
  // after a delay for hatch processing, show disabled button for any missing buttons
  config.hatch_timeout = setTimeout(() => {
    // identify unavailable buttons
    let unavailable = container.find('[data-gh-buy-now-button]').filter((i, e) => $(e).css('visibility') === 'hidden');
    unavailable.hide(0);
    unavailable.next().show(0);
  }, 5000);
};

/**
 * Start [Matrix]. Handles the display of relevant buttons once matrix results are complete.
 *
 * *Note that the `env` setting is temporarily set to `matrix` during this process, but is returned to the previous state once complete.*
 *
 * @since 3.4.2
 *
 * @param {jQuery} button - The matrix trigger button element.
 *
 * @see [`Matrix.init`]{@link module:core/matrix.init}
 * @see [`App.updateSettings`]{@link module:core/app.updateSettings}
 * @see Used by:
 * @see [**`addEventListeners`**]{@link module:core/view~addEventListeners}
 */
const runMatrix = (button) => {
  // if matrix button is already disabled, do nothing
  if (button.attr('aria-disabled') === 'true') return;
  // get current env
  let env = App.settings.env;
  // find relevant elements
  let resultsContainer = container.find('[data-matrix-results]');
  let resultsButtons = resultsContainer.find('button');
  // disable matrix button and update with loading message
  button.attr('aria-disabled', 'true').text('This could take a while...');
  button.addClass('u-loadingSpinner');
  // initialize matrix
  // set on a delay so the visual elements have a chance to render
  setTimeout(() => {
    Matrix.init()
      .then(() => {
        // actions to run once complete
        button.remove();
        resultsButtons.attr('aria-disabled', 'false');
        App.updateSettings({env});
      });
  }, 1000);
};

/**
 * Render online product page in a modal popup based on a provided link.
 *
 * **Note that for the modal product page to work, the `modal_product` [view setting]{@link module:core/config.view} must be enabled, a button with class `js-productModalButton` must be present in the product content template, and relevant project templates must include either the `core-product-modal` partial, or a custom popup partial with the class `js-productModal`.**
 *
 * @since 3.5.0
 *
 * @param {string} url - The URL for the desired content.
 *
 * @see [`togglePopup`]{@link module:core/view.togglePopup}
 * @see Used by:
 * @see [**`addEventListeners`**]{@link module:core/view~addEventListeners}
 *
 * @example
 * <!-- include trigger button in product content template -->
 * <button class=".js-productModalButton">View product</button>
 *
 * <!-- include popup template partial in recommend or product content templates -->
 * {{> core-product-modal }}
 */
const productModal = (url = '') => {
  // do nothing if modal product not enabled
  if (!App.settings.view.modal_product) return;
  // get product page modal
  let content = $('.js-productModal');
  // do nothing if no product modal element is found
  if (!content.length) return;
  // set modal iframe
  content.html(`<iframe src="${url}"></iframe>`);
  // toggle popup
  togglePopup(true, '.js-productModal');
};

/**
 * Append additional product results to product container. Some conditions are required for this functionality to work:
 *
 * - The [view setting]{@link module:core/config.view} `show_more_products` must be enabled.
 * - The current view content must contain additional products in the `productsShowMore` property.
 * - A button with class `.js-productsShowMoreButton` with parent element `.js-productsShowMore` must exist inside the products container. This can be added to a products template with the core view partial `{{> core-products-show-more }}`.
 *
 * @param {jQuery} button - Button to trigger showing additional products.
 *
 * @see [`setContainer`]{@link module:core/view~setContainer}
 * @see [`updateRenderState`]{@link module:core/view~updateRenderState}
 * @see [`renderContentTo`]{@link module:core/view.renderContentTo}
 * @see [`postRenderViewActions`]{@link module:core/view~postRenderViewActions}
 * @see Used by:
 * @see [**`addEventListeners`**]{@link module:core/view~addEventListeners}
 */
const showMoreProducts = (button) => {
  // if show more products is not enabled, or there are no additional products, do nothing
  if (!App.settings.view.show_more_products.enabled || !App.current.productsShowMore.length) return;
  // get button container
  let buttonContainer = button.parents('.js-productsShowMore');
  // set current container to the parent of the button container, which should be the product container
  setContainer(buttonContainer.parent());
  // remove current button
  buttonContainer.remove();
  // get additional products from the first array in the `productsShowMore` array
  let products = App.current.productsShowMore.shift();
  // append additional products to current content products
  App.current.products = App.current.products.concat(products);
  // create render data object
  let renderData = {
    settings: App.current.settings,
    products,
    productsShowMore: App.current.productsShowMore,
  };
  // remove fadeIn class from existing products to prevent animation
  container.find('.fadeIn').removeClass('fadeIn');
  // update rendering state
  updateRenderState('rendering', true);
  // render additional products to container
  renderContentTo(container[0], 'core-products', renderData, true);
  // run post-render view actions
  postRenderViewActions(false);
};

/**
 * When enabled, reload products into the products content type container, if the requested products have changed. To enable this functionality, the `reload_products_on_state_change` [view setting]{@link module:core/config.view} must be enabled. This functionality is also not run if there are no products in the current view content, or on the app's initial load. An overview of the process:
 *
 * - Get list of current product IDs (before updates), then [update content]{@link module:core/app.updateContent} and compare new list of product IDs. If products list hasn't changed, stop here.
 * - [Set current container]{@link module:core/view~setContainer} to the product content type element `.p-contentType-products`.
 * - [Update render state]{@link module:core/view~updateRenderState}, then [render updated current content]{@link module:core/view.renderContentTo} to the products element, using the `core-content-products` template partial.
 * - [Run post-render view actions]{@link module:core/view~postRenderViewActions} (non-nav).
 *
 * Note that all of the above functionality is wrapped in a debounced function as `config.debounce.reloadProducts` to mitigate unnecessary close-proximity execution.
 *
 * @see [`App.updateContent`]{@link module:core/app.updateContent}
 * @see [`setContainer`]{@link module:core/view~setContainer}
 * @see [`updateRenderState`]{@link module:core/view~updateRenderState}
 * @see [`renderContentTo`]{@link module:core/view.renderContentTo}
 * @see [`postRenderViewActions`]{@link module:core/view~postRenderViewActions}
 * @see Used by:
 * @see [**`updateState`**]{@link module:core/view.updateState}
 */
const reloadProducts = () => {
  if (!App.current.products || !App.current.settings.view.reload_products_on_state_change || App.settings.initial_load) return;
  // set debounce reload, if not already set
  if (!config.debounce.reloadProducts) config.debounce.reloadProducts = Util.debounce(() => {
    // get current products list, sliced to request quantity
    let currentProducts = App.current.products.slice(0, App.current.productMeta.requestQuantity);
    // update view content
    App.updateContent();
    // if products list within request amount hasn't changed, stop here
    if (Util.stringifyCompare(currentProducts, App.current.products)) return;
    // get products container
    let productsContainer = $('.p-contentType-products');
    // set current container to the products container
    setContainer(productsContainer);
    // re-render products
    updateRenderState('rendering', true);
    renderContentTo(productsContainer, 'core-content-products', App.current);
    postRenderViewActions(false);
  }, 500);
  // run debounced reload function
  config.debounce.reloadProducts();
};

/**
 * Send matrix results to the [SRT Recommendation Matrix tool]{@link https://srt-dev.test/tools/recommend-matrix/} for CSV download.
 *
 * @since 3.4.2
 *
 * @see Used by:
 * @see [**`addEventListeners`**]{@link module:core/view~addEventListeners}
 */
const submitMatrixResults = () => {
  // get matrix form
  let form = container.find('#matrixResults');

  // define parameters to send
  const params = {
    matrix: JSON.stringify(App.settings.matrix.results),
  };

  // add hidden input for each parameter
  form.append($.map(Object.keys(params), function (param) {
    return $('<input>', {
      type: 'hidden',
      name: param,
      value: params[param],
    })
  }));

  // submit form
  form.submit();
};

/**
 * Initialize and render blueprint.
 *
 * - Set [`container`]{@link module:core/view.container} to `body`.
 * - [Re-initialize event listeners]{@link module:core/view~addEventListeners}.
 * - [Render blueprint data to `container`]{@link module:core/view.renderContentTo}, using the `core-blueprint` template partial.
 * - [Remove duplicate blueprint nodes]{@link module:core/view~removeBlueprintDuplicateNodes}.
 * - [Render blueprint path elements]{@link module:core/view~renderBlueprintPaths}.
 * - [Add blueprint node resize listeners]{@link module:core/view~resizeBlueprint}.
 *
 * @see [`setContainer`]{@link module:core/view~setContainer}
 * @see [`addEventListeners`]{@link module:core/view~addEventListeners}
 * @see [`renderContentTo`]{@link module:core/view.renderContentTo}
 * @see [`removeBlueprintDuplicateNodes`]{@link module:core/view~removeBlueprintDuplicateNodes}
 * @see [`renderBlueprintPaths`]{@link module:core/view~renderBlueprintPaths}
 * @see [`resizeBlueprint`]{@link module:core/view~resizeBlueprint}
 * @see Used by:
 * @see [**`addEventListeners`**]{@link module:core/view~addEventListeners}
 */
const blueprintInit = () => {
  let data = App.settings.matrix.blueprint;
  setContainer(container.parent());
  addEventListeners();
  renderContentTo(container, 'core-blueprint', data);
  removeBlueprintDuplicateNodes();
  renderBlueprintPaths(data);
  resizeBlueprint();
};

/**
 * Remove duplicate blueprint nodes.
 *
 * Blueprint nodes may be rendered more than once if multiple paths lead to the same node. This method removes duplicates so only the first node remains.
 *
 * @see Used by:
 * @see [**`blueprintInit`**]{@link module:core/view~blueprintInit}
 */
const removeBlueprintDuplicateNodes = () => {
  let nodes = [];
  // remove duplicate nodes
  $('.js-blueprint-node').each((i, e) => {
    let el = $(e);
    let id = el.attr('id');
    nodes.includes(id) ? el.remove() : nodes.push(id);
  });
};

/**
 * Create and position blueprint path elements.
 *
 * For each node, destinations and side journeys are collected from the element data, the relative position of the exit and entry nodes is calculated, and the path elements are drawn. If a given path element already exists, it will be repositioned based on any changed positions of the relevant nodes.
 *
 * @see Used by:
 * @see [**`blueprintInit`**]{@link module:core/view~blueprintInit}
 */
const renderBlueprintPaths = () => {
  // get content container
  let content = $('.js-blueprint-content');
  let nodes = $('.js-blueprint-node');
  let pathIndex = 0;

  // loop through nodes to process side journeys
  let sObjs = [];
  nodes.each((i, e) => {
    let el = $(e);
    if (el.attr('data-side-journeys')) sObjs.push(el);
  });
  for (let el of sObjs) {
    let sjs = el.attr('data-side-journeys').split(',');
    for (let i = 0; i < sjs.length; i++) {
      // define path style
      let pathY = 'down';
      let pathX = 'flat';
      let translateY = 0;
      let translateX = 0;
      let sj = sjs[i];
      // get side journey path exit position
      let elPosition = el.offset();
      let elHeight = el.outerHeight();
      let exitX = elPosition.left + (i * 32) + 32;
      let exitY = elPosition.top + elHeight;
      // get path entry position
      let sjEl = $(`#${sj}`);
      let sjElPosition = sjEl.offset();
      let entryXDistance = sjElPosition.left - exitX + 32;
      let entryYDistance = sjElPosition.top - exitY;
      // reposition path if height is negative
      if (entryYDistance < 0) {
        entryYDistance = Math.abs(entryYDistance);
        translateY = -entryYDistance;
        pathY = 'up';
      }
      // reposition path if width is negative
      if (entryXDistance < 0) {
        entryXDistance = Math.abs(entryXDistance);
        translateX = -entryXDistance;
        pathX = 'backward';
      }
      else if (entryXDistance > 0) pathX = 'forward';

      // update or add path element
      let path = content.children().eq(pathIndex);
      // create path element if it doesn't exist
      if (!path.length) {
        path = $(`<div class="p-blueprint__path__path js-blueprint-path" data-path-type="side-journey"><div></div></div>`);
        content.append(path);
        // increment side journey node entry count
        let sje = parseInt(sjEl.attr('data-side-journey-entries'));
        sjEl.attr('data-side-journey-entries', sje + 1);
      }
      // update path attributes
      path.attr({
        'data-path-index': i,
        'data-path-x': pathX,
        'data-path-y': pathY,
        'style': `top: ${exitY}px; left: ${exitX}px; width: ${entryXDistance}px; height: ${entryYDistance}px; transform: translate(${translateX}px, ${translateY}px)`,
      });

      // increment path index
      pathIndex++;
    }
  }

  // create array of destination objects
  let dObjs = [];
  nodes.each((i, e) => {
    let el = $(e);
    if (el.attr('data-destination')) dObjs.push(el);
    else {
      el.find('.js-blueprintSection').each((i, e) => {
        let section = $(e);
        if (section.attr('data-destination')) dObjs.push(section);
        else {
          el.find('[data-destination]').each((i, e) => {
            dObjs.push($(e));
          });
        }
      });
    }
  });

  // get positions of destination paths
  for (let el of dObjs) {
    // define path style
    let pathY = 'flat';
    let pathX = 'forward';
    let translateY = 0;
    let translateX = 0;
    // define element type
    let isChoice = el.is('.js-blueprint-choice');
    // get path exit position
    let elPosition = el.offset();
    let elWidth = el.outerWidth();
    let elHeight = el.outerHeight();
    let exitX = elPosition.left + elWidth;
    let exitY = isChoice ? elPosition.top + (elHeight / 2) : elPosition.top + 32;
    // get path entry position
    let destination = el.attr('data-destination');
    let dEl = $(`#${destination}`);
    let dElPosition = dEl.offset();
    let entryXDistance = dElPosition.left - exitX;
    let entryYDistance = dElPosition.top - exitY + 32;
    // reposition path if height is negative
    if (entryYDistance < 0) {
      entryYDistance = Math.abs(entryYDistance);
      translateY = -entryYDistance;
      pathY = 'up';
    }
    else if (entryYDistance > 0) pathY = 'down';
    // reposition path if width is negative
    if (entryXDistance < 0) {
      entryXDistance = Math.abs(entryXDistance);
      translateX = -entryXDistance;
      pathX = 'backward';
    }

    // update or add path element
    let path = content.children().eq(pathIndex);
    // create path element if it doesn't exist
    if (!path.length) {
      path = $(`<div class="p-blueprint__path__path js-blueprint-path" data-path-type="destination"><div></div></div>`);
      content.append(path);
    }
    // update path attributes
    path.attr({
      'data-path-x': pathX,
      'data-path-y': pathY,
      'data-path-is-choice': isChoice,
      'style': `top: ${exitY}px; left: ${exitX}px; width: ${entryXDistance}px; height: ${entryYDistance}px; transform: translate(${translateX}px, ${translateY}px)`,
    });

    // increment path index
    pathIndex++;
  }
};

/**
 * Add `ResizeObserver` to `.js-blueprint-node` elements, to redraw paths on any node resize events.
 *
 * @see [`renderBlueprintPaths`]{@link module:core/view~renderBlueprintPaths}
 * @see Used by:
 * @see [**`blueprintInit`**]{@link module:core/view~blueprintInit}
 */
const resizeBlueprint = () => {
  let nodes = document.querySelectorAll('.js-blueprint-node');
  nodes.forEach(el => {
    new ResizeObserver(Util.debounce(renderBlueprintPaths, 10, {maxWait: 25, leading: true, trailing: false})).observe(el);
  })
};

/**
 * Actions to run on window resize, including setting [dimensions]{@link module:core/view.dimensions} and running any project resize functions.
 *
 * Note that resize functions are run through a [debouncing]{@link http://unscriptable.com/index.php/2009/03/20/debouncing-javascript-methods/} function (in [`addEventListeners`]{@link module:core/view~addEventListeners}) to limit the number of calls firing during a resize.
 *
 * @since 3.0.0
 *
 * @see [`dimensions`]{@link module:core/view.dimensions}
 * @see [`App.customize`]{@link module:core/app.customize}
 * @see Used by:
 * @see [**`addEventListeners`**]{@link module:core/view~addEventListeners}
 */
const resize = () => {
  // set dimensions
  dimensions.set();

  App.customize('resize');
};

/**
 * Control filter weights input and value states for visualization screens.
 *
 * @namespace
 *
 * @type {object}
 */
const filterWeights = {
  /**
   * Show filter weights test popup.
   *
   * - Clone filter weights inputs into popup form container.
   * - [Get weighted filter attributes]{@link module:core/filter.getWeightedProductAttributes} and [render]{@link module:core/view.renderContentTo} to popup values container.
   * - [Show popup]{@link module:core/view.togglePopup}.
   *
   * @memberOf module:core/view~filterWeights
   *
   * @param {jQuery} testButton - jQuery object containing test button element.
   *
   * @see [`Filter.getWeightedProductAttributes`]{@link module:core/filter.getWeightedProductAttributes}
   * @see [`renderContentTo`]{@link module:core/view.renderContentTo}
   * @see [`togglePopup`]{@link module:core/view.togglePopup}
   * @see Used by:
   * @see [**`addEventListeners`**]{@link module:core/view~addEventListeners}
   */
  popup: (testButton) => {
    // get attribute elements
    let attrContainer = testButton.parent('[data-debug-filter-weights-attribute]');
    let attrHeader = attrContainer.find('.js-filterWeightsHeader');
    let attrWeights = attrContainer.find('.js-filterWeightsField');
    let attribute = attrContainer.attr('data-debug-filter-weights-attribute');
    // get popup elements
    let popup = container.find('.js-popupFilterWeights');
    let header = popup.find('.js-popupFilterWeightsHeader');
    let form = popup.find('.js-popupFilterWeightsForm');
    let valuesContainer = popup.find('.js-popupFilterWeightsValues');
    // let update = popup.find('.js-popupFilterWeightsUpdate');
    // render elements to popup
    header.html('').html(attrHeader.clone());
    form.html('').html(attrWeights.clone());
    let values = Filter.getWeightedProductAttributes(attribute);
    renderContentTo(valuesContainer, 'core-debug-filter-weights-test-values', {attribute, values});
    togglePopup(true, '.js-popupFilterWeights');
  },

  /**
   * For a given filter weight input, update the associated filter weight value to the current input value.
   *
   * - Compare current and original values, add `input-modified` class to input parent if different.
   * - Update filter schema settings with the appropriate attribute and weight value setting.
   *
   * @memberOf module:core/view~filterWeights
   *
   * @param {jQuery} input - jQuery object for the input to update.
   *
   * @see [`filterWeights.updateValue`]{@link module:core/view~filterWeights.updateValue}
   * @see Used by:
   * @see [**`addEventListeners`**]{@link module:core/view~addEventListeners}
   */
  updateWeightInputValue: (input) => {
    let inputID = input.attr('id');
    let item = input.parents('[data-debug-filter-weights-value]');
    let startValue = input.attr('data-starting-value');
    let currentValue;
    if (input.is('[type="checkbox"]')) {
      startValue = !!startValue;
      currentValue = input.is(':checked');
    }
    else {
      currentValue = input.val() || false;
      // parse number input values into numbers
      if (input.is('[type="number"]')) {
        currentValue = parseFloat(currentValue) || false;
        startValue = parseFloat(startValue) || false;
      }
    }
    item.toggleClass('input-modified', currentValue !== startValue);
    // if this is a popup input, copy changes back to main input
    if (item.parents('.js-popupFilterWeights').length) {
      let mainItem = container.find(`.js-filterWeights #${inputID}`).parent();
      mainItem.replaceWith(item.clone());
    }
    // update settings value
    let names = inputID.split('-');
    filterWeights.updateValue(names[1], names[2], currentValue)
  },

  /**
   * Set filter weight property value for the given attribute.
   *
   * A special case exists if the property to update is `prioritize`, where instead of updating the attribute's filter weight values, the provided value will be set to the filter options `prioritize` property.
   *
   * @memberOf module:core/view~filterWeights
   * 
   * @param {string} attribute - Attribute name.
   * @param {string} property - Attribute weights property name.
   * @param {*} value - Weight property value to store.
   *
   * @see [`App.updateSettings`]{@link module:core/app.updateSettings}
   * @see Used by:
   * @see [**`filterWeights.updateWeightInputValue`**]{@link module:core/view~filterWeights.updateWeightInputValue}
   * @see [**`filterWeights.resetInputs`**]{@link module:core/view~filterWeights.resetInputs}
   */
  updateValue: (attribute, property, value) => {
    console.log({attribute, property, value});
    let data = (property === 'prioritize') ? { filter: { filter_options: { prioritize: { [attribute]: value } } } } : { filter: { schema: { [attribute]: { weights: { [property]: value } } } } };
    App.updateSettings(data);
    console.log(App.settings.filter);
  },

  /**
   * For a given test value button, re-render weighted values to filter weights popup.
   *
   * @memberOf module:core/view~filterWeights
   *
   * @param {jQuery} button - jQuery object containing test button element.
   *
   * @see [`renderContentTo`]{@link module:core/view.renderContentTo}
   * @see Used by:
   * @see [**`addEventListeners`**]{@link module:core/view~addEventListeners}
   */
  updateTestValues: (button) => {
    let attribute = button.attr('data-test-attribute');
    let value = button.attr('data-test-attribute-value');
    let popup = container.find('.js-popupFilterWeights');
    let valuesContainer = popup.find('.js-popupFilterWeightsValues');
    let values = Filter.getWeightedProductAttributes(attribute, value);
    renderContentTo(valuesContainer, 'core-debug-filter-weights-test-values', {attribute, values});
  },

  /**
   * Reset an attribute's filter weight inputs to original values.
   *
   * @memberOf module:core/view~filterWeights
   *
   * @param {jQuery} resetButton - jQuery object containing reset button element.
   *
   * @see [`filterWeights.updateValue`]{@link module:core/view~filterWeights.updateValue}
   * @see Used by:
   * @see [**`addEventListeners`**]{@link module:core/view~addEventListeners}
   */
  resetInputs: (resetButton) => {
    resetButton.parent('[data-debug-filter-weights-attribute]').find('input').each((i, e) => {
      let input = $(e);
      let startValue = input.attr('data-starting-value');
      if (input.is('[type="checkbox"]')) {
        startValue = !!startValue;
        input.prop('checked', startValue);
      }
      else {
        startValue = parseFloat(startValue) || '';
        input.val(startValue)
      }
      input.parent('.input-modified').removeClass('input-modified');
      // update settings value
      let names = input.attr('id').split('-');
      filterWeights.updateValue(names[1], names[2], startValue)
    });
  },
};

/**
 * Manage accordion interactions. See content settings for [accordion field types]{@link module:core/config._content_type_accordion} for usage information.
 *
 * @namespace
 *
 * @since 3.0.0
 *
 * @type {object}
 */
export const accordion = {
  /**
   * Event listeners for accordion interaction.
   *
   * - [Toggle accordion expansion]{@link module:core/view.accordion.toggle} on `.js-accordionTrigger` click.
   * - Run [keydown events]{@link module:core/view.accordion.keydownEvents} on key presses on `.js-accordionTrigger` elements.
   * - [Expand all accordion sections]{@link module:core/view.accordion.expandAll} on `.js-accordionExpandAll` click.
   * - [Collapse all accordion sections]{@link module:core/view.accordion.collapseAll} on `.js-accordionCollapseAll` click.
   *
   * *Note that expand/collapse all elements are not present by default and must be added to templates where desired.*
   *
   * @memberOf module:core/view.accordion
   *
   * @since 3.0.0
   *
   * @see [`accordion.toggle`]{@link module:core/view.accordion.toggle}
   * @see [`accordion.keydownEvents`]{@link module:core/view.accordion.keydownEvents}
   * @see [`accordion.expandAll`]{@link module:core/view.accordion.expandAll}
   * @see [`accordion.collapseAll`]{@link module:core/view.accordion.collapseAll}
   * @see Used by:
   * @see [**`addEventListeners`**]{@link module:core/view~addEventListeners}
   */
  addEventListeners: () => {
    // accordion expand
    container.on('click', '.js-accordionTrigger', (event) => {
      accordion.toggle($(event.currentTarget));
    });

    // accordion keypress listeners
    container.on('keydown', '.js-accordionTrigger', (event) => {
      accordion.keydownEvents(event);
    });

    // expand all accordions
    container.on('click', '.js-accordionExpandAll', () => {
      accordion.expandAll(container.find('.js-accordionSection'));
    });

    // collapse all accordions
    container.on('click', '.js-accordionCollapseAll', () => {
      accordion.collapseAll(container.find('.js-accordionSection'));
    });
  },

  /**
   * Initialize accordions.
   *
   * By default, all sections are collapsed except for the first. However, if the `collapse_on_start` [accordion setting]{@link module:core/config._content_type_accordion} is used, all sections will be collapsed.
   *
   * Note that in both cases, any sections containing active inputs will remain open, regardless of accordion settings.
   *
   * @memberOf module:core/view.accordion
   *
   * @since 3.0.0
   *
   * @param {jQuery} [items] - Accordions to initialize.  Default: `container.find('.js-accordion')`
   *
   * @see [`accordion.collapse`]{@link module:core/view.accordion.collapse}
   * @see [`accordion.expand`]{@link module:core/view.accordion.expand}
   * @see Used by:
   * @see [**`postRenderViewActions`**]{@link module:core/view~postRenderViewActions}
   */
  init: (items = container.find('.js-accordion')) => {
    items.each((i, item) => {
      const group = $(item);
      const sections = group.find('.js-accordionSection');
      const startCollapsed = group.is('[data-collapse-on-start]');

      // collapse each section, with no animation
      accordion.collapseAll(sections, false);

      // expand any necessary sections
      // sections with active inputs
      let activeSections = sections.filter((i, s) => getActiveInputs($(s)).length);
      // if not starting collapsed, add first input to active
      if (!startCollapsed) activeSections = activeSections.add(sections.filter(':not(.hidden)').first());
      // expand active sections
      accordion.expandAll(activeSections, false);
    });
  },

  /**
   * Hide accordion sections which do not contain any visible inputs, if the `require_inputs` [accordion setting]{@link module:core/config._content_type_accordion} is enabled.
   *
   * @memberOf module:core/view.accordion
   *
   * @since 3.4.6
   *
   * @see Used by:
   * @see [**`removeUnusedElements`**]{@link module:core/view~removeUnusedElements}
   */
  removeEmpty: () => {
    container.find('.js-accordionSection[data-require-inputs]').each((i, e) => {
      if (!$(e).find('.input').length) $(e).remove();
    });
  },

  /**
   * Collapse all provided accordion sections.
   *
   * @memberOf module:core/view.accordion
   *
   * @since 3.4.2
   *
   * @param {jQuery} sections - Accordion sections to collapse.
   * @param {boolean} animate - When enabled, section collapse will be animated. Default: `true`
   *
   * @see [`accordion.collapse`]{@link module:core/view.accordion.collapse}
   * @see Used by:
   * @see [**`accordion.addEventListeners`**]{@link module:core/view.accordion.addEventListeners}
   */
  collapseAll: (sections, animate = true) => {
    sections.each((i, e) => {
      accordion.collapse($(e), animate);
    });
  },

  /**
   * Collapse accordion content section.
   *
   * - Trigger element `.js-accordionTrigger` gets attribute `aria-expanded: false` set.
   * - Content element `.js-accordionContent` is either animated closed or set to `display: none`, depending on the `animate` param.
   * - [Update active input count.]{@link module:core/view.accordion.updateActiveInputCount}
   *
   * @memberOf module:core/view.accordion
   *
   * @since 3.0.0
   *
   * @param {jQuery} section - Accordion content section to collapse (`.js-accordionSection`).
   * @param {boolean} [animate] - Whether the section collapse should be animated, or simply hidden. Default: `true`
   *
   * @see [`accordion.updateActiveInputCount`]{@link module:core/view.accordion.updateActiveInputCount}
   * @see Used by:
   * @see [**`accordion.init`**]{@link module:core/view.accordion.init}
   * @see [**`accordion.toggle`**]{@link module:core/view.accordion.toggle}
   */
  collapse: (section, animate = true) => {
    const trigger = section.find('.js-accordionTrigger');
    const content = section.find('.js-accordionContent');
    trigger.attr('aria-expanded', false);
    animate ? content.slideUp(250) : content.css('display', 'none');
    // update active input count
    accordion.updateActiveInputCount(section);
  },


  /**
   * Expand all provided accordion sections.
   *
   * @memberOf module:core/view.accordion
   *
   * @since 3.4.2
   *
   * @param {jQuery} sections - Accordion sections to expand.
   * @param {boolean} animate - When enabled, section expand will be animated. Default: `true`
   *
   * @see [`accordion.expand`]{@link module:core/view.accordion.expand}
   * @see Used by:
   * @see [**`accordion.addEventListeners`**]{@link module:core/view.accordion.addEventListeners}
   */
  expandAll: (sections, animate = true) => {
    sections.each((i, e) => {
      accordion.expand($(e), animate);
    });
  },

  /**
   * Expand accordion content section.
   *
   * - Trigger element `.js-accordionTrigger` gets attribute `aria-expanded: true` set.
   * - Content element `.js-accordionContent` is either animated open or set to `display: block`, depending on the `animate` param.
   * - [Update active input count.]{@link module:core/view.accordion.updateActiveInputCount}
   *
   * @memberOf module:core/view.accordion
   *
   * @since 3.0.0
   *
   * @param {jQuery} section - Accordion content section to expand (`.js-accordionSection`).
   * @param {boolean} [animate] - Whether the section expansion should be animated, or simply shown. Default: `true`
   *
   * @see [`accordion.updateActiveInputCount`]{@link module:core/view.accordion.updateActiveInputCount}
   * @see Used by:
   * @see [**`accordion.init`**]{@link module:core/view.accordion.init}
   * @see [**`accordion.toggle`**]{@link module:core/view.accordion.toggle}
   */
  expand: (section, animate = true) => {
    const trigger = section.find('.js-accordionTrigger').first();
    const content = section.find('.js-accordionContent').first();
    trigger.attr('aria-expanded', true);
    if (animate) {
      content.slideDown(250)
      // send analytics event
      Analytics.event('toggle_click');
    }
    else content.css('display', 'block');
    // update active input count
    accordion.updateActiveInputCount(section, false);
  },


  /**
   * Update the active input count text in an accordion header.
   *
   * If accordion contains active inputs, the active input count is added to the title of the accordion trigger when collapsed.
   *
   * @memberOf module:core/view.accordion
   *
   * @since 3.3.1
   *
   * @param {jQuery} sections - Accordion content section(s) to update.
   * @param {boolean} [show] - Whether the active input count should be hidden or shown. Default: `true`
   *
   * @see Used by:
   * @see [**`accordion.expand`**]{@link module:core/view.accordion.expand}
   * @see [**`accordion.collapse`**]{@link module:core/view.accordion.collapse}
   */
  updateActiveInputCount: (sections, show = true) => {
    sections.each((i, e) => {
      let section = $(e);
      let element = section.find('.js-accordion-activeInputCount');
      let count = getActiveInputs(section).length;
      // always remove previous input count
      element.attr('aria-label', '').html('');
      // if not hiding or no active inputs, stop here
      if (!show || !count) return;
      // otherwise update label
      let aria = App.settings.labels.aria_accordion_options_selected || '';
      element.attr('aria-label', `${count} ${aria}`).html(`(${count})`);
    });
  },

  /**
   * Toggle the state of a content section after interaction with a section trigger element.
   *
   * If section is expanded, it is [collapsed]{@link module:core/view.accordion.collapse}.
   *
   * If a section is collapsed, all other sections within the accordion are [collapsed]{@link module:core/view.accordion.collapse}, and the current section is [expanded]{@link module:core/view.accordion.expand}
   *
   * @memberOf module:core/view.accordion
   *
   * @since 3.0.0
   *
   * @param {jQuery} trigger - Accordion trigger object (element with class `.js-accordionTrigger`).
   *
   * @see [`accordion.collapse`]{@link module:core/view.accordion.collapse}
   * @see [`accordion.expand`]{@link module:core/view.accordion.expand}
   * @see Used by:
   * @see [**`accordion.addEventListeners`**]{@link module:core/view.accordion.addEventListeners}
   */
  toggle: (trigger) => {
    const section = trigger.closest('.js-accordionSection');
    const isExpanded = trigger.is('[aria-expanded="true"]');

    if (isExpanded) {
      accordion.collapse(section);
      return;
    }

    const group = trigger.closest('.js-accordion');
    const isMultiple = group.is('[data-expand-multiple]');
    if (!isMultiple) accordion.collapse(group.find('.js-accordionSection'));
    accordion.expand(section);
  },

  /**
   * Manage keyboard interactions within accordions. Follows guidelines in W3's [Accordion Example]{@link https://www.w3.org/WAI/ARIA/apg/example-index/accordion/accordion.html} for how users should be able to interact with accordion elements.
   *
   * All keydown events within accordion content areas are routed through this function, and any necessary actions are taken. Note that these actions will only occur if focus is currently on a `.js-accordionTrigger` section trigger, and nothing will change if focus is within the accordion content..
   *
   * - `down` or `up` - Focus on the next or previous section header, respectively. Focus will wrap around to other end of list if either end is reached.
   *
   * - `home` or `end` - Focus on the first or last section header, respectively.
   *
   * @memberOf module:core/view.accordion
   *
   * @since 3.0.0
   *
   * @param {object} event - jQuery event object.
   *
   * @see Used by:
   * @see [**`accordion.addEventListeners`**]{@link module:core/view.accordion.addEventListeners}
   */
  keydownEvents: (event) => {
    const trigger = $(event.currentTarget);
    const group = trigger.closest('.js-accordion');
    const sections = group.find('.js-accordionSection');
    const focusedIndex = sections.index(trigger.closest('.js-accordionSection'));
    let nextIndex;

    // up arrow
    if (event.keyCode === keycode.up) {
      nextIndex = focusedIndex - 1;
      if (focusedIndex === 0) {
        nextIndex = sections.length - 1;
      }
    }

    // down arrow
    if (event.keyCode === keycode.down) {
      nextIndex = focusedIndex + 1;
      if (focusedIndex === sections.length - 1) {
        nextIndex = 0;
      }
    }

    // home key
    if (event.keyCode === keycode.home) {
      nextIndex = 0;
    }

    // end key
    if (event.keyCode === keycode.end) {
      nextIndex = sections.length - 1;
    }

    sections.eq(nextIndex).find('.js-accordionTrigger').focus();
  },
};

/**
 * Manage tabbed content interactions. See content settings for [tab field types]{@link module:core/config._content_type_tabs} for usage information.
 *
 * @namespace
 *
 * @since 3.0.0
 *
 * @type {object}
 */
export const tabs = {
  /**
   * Event listeners for tabs interaction.
   *
   * - [Open content related to tab]{@link module:core/view.tabs.open} on `.js-tab` click.
   *
   * @memberOf module:core/view.tabs
   *
   * @since 3.0.0
   *
   * @see [`tabs.open`]{@link module:core/view.tabs.open}
   * @see Used by:
   * @see [**`addEventListeners`**]{@link module:core/view~addEventListeners}
   */
  addEventListeners: () => {
    container.on('click', '.js-tab', (event) => {
      tabs.open($(event.currentTarget));
    });
  },

  /**
   * Display tab content.
   *
   * Opening a tab consists of setting an `active` class on the tab (`.js-tab` element) and tab content (`.js-tabPanel`), and removed from any previously active tabs.
   *
   * Tab states are saved in the current node, so they can be restored when traveling back to the view. When tab states are changed, all active tabs in the view are gathered with [`active`]{@link module:core/view.tabs.active} and [stored in the current node]{@link module:core/app.updatePathNode}.
   *
   * @memberOf module:core/view.tabs
   *
   * @since 3.0.0
   *
   * @param {jQuery} tab - Tab jQuery object (element with class `.js-tab`).
   * @param {boolean} [passive] - When enabled, node will not be updated when tab is activated. Default: `false`
   *
   * @see [`tabs.active`]{@link module:core/view.tabs.active}
   * @see [`App.updatePathNode`]{@link module:core/app.updatePathNode}
   * @see Used by:
   * @see [**`tabs.addEventListeners`**]{@link module:core/view.tabs.addEventListeners}
   * @see [**`setActiveSelections`**]{@link module:core/view~setActiveSelections}
   */
  open: (tab, passive = false) => {
    // if tab is already active, do nothing
    if (tab.hasClass('active')) return;

    const tabsOuter = tab.closest('.js-tabs');
    const tabIndex = tab.index();
    const tabItems = tabsOuter.find('.js-tab');
    const tabPanels = tabsOuter.find('.js-tabPanel');

    tabItems.removeClass('active');
    tabPanels.removeClass('active').attr('aria-hidden', 'true');
    tab.addClass('active');
    tabPanels.eq(tabIndex).addClass('active').attr('aria-hidden', 'false');

    // if passive mode, don't continue
    if (passive) return;

    const activeTabs = tabs.active();
    if (activeTabs.length) App.updatePathNode({tabs: activeTabs});
  },

  /**
   * Find all active tabs. Returns array of indices representing the position of each active tab within a tab group.
   *
   * @memberOf module:core/view.tabs
   *
   * @since 3.0.0
   *
   * @return {array}
   *
   * @see Used by:
   * @see [**`tabs.open`**]{@link module:core/view.tabs.open}
   *
   * @example
   * <div id="tab_group_1" class="js-tabs">
   *   <div class="js-tab">...</div>
   *   <div class="js-tab">...</div>
   *   <div class="js-tab active">...</div>
   * </div>
   *
   * <div id="tab_group_2" class="js-tabs">
   *   <div class="js-tab active">...</div>
   *   <div class="js-tab">...</div>
   * </div>
   *
   * @example
   * tabs.active();
   * // expected result
   * [2, 0]
   *
   */
  active: () => {
    const tabGroups = container.find('.js-tabs');
    let selections = [];

    // return empty array if there are no active tabs
    if (!tabGroups.length) return selections;

    tabGroups.each((i, element) => {
      const items = $(element).find('.js-tab');
      const activeTab = items.filter('.active');
      const activeTabIndex = items.index(activeTab);
      selections.push(activeTabIndex);
    });

    return selections;
  },
};

/**
 * Manage facet filter-related view functionality. Information about configuring facet filtering can be found in the [`facet`]{@link module:core/config.facet} setting.
 *
 * @namespace
 *
 * @since 3.0.0
 *
 * @type {object}
 */
export const facet = {
  /**
   * Add facet filter event listeners.
   *
   * - [Reset facet filter state]{@link module:core/view.facet.reset} and run [`filter_reset` analytics event]{@link module:core/analytics.event} on `.js-clearFilters` click.
   *
   * @memberOf module:core/view.facet
   *
   * @since 3.0.0
   *
   * @see [`Analytics.event`]{@link module:core/analytics.event}
   * @see [`facet.reset`]{@link module:core/view.facet.reset}
   * @see Used by:
   * @see [**`addEventListeners`**]{@link module:core/view~addEventListeners}
   */
  addEventListeners: () => {
    // remove all filters
    container.on('click', '.js-clearFilters', () => {
      Analytics.event('filter_reset');
      facet.reset();
    });
  },

  /**
   * This object stores all available facet filter attributes for a given view. This collection of attributes represents all possible choices a user can make, and is called the "facet object" in many places. Facet objects are [stored under and retrieved from]{@link module:core/view.facet.getObject} the property name of the current view.
   *
   * @memberOf module:core/view.facet
   *
   * @since 3.0.0
   *
   * @type {object}
   *
   * @see Used by:
   * @see [**`facet.getObject`**]{@link module:core/view.facet.getObject}
   *
   * @example
   * attributes: {
   *   specs: {
   *     form_factor: ['notebook', 'desktop'],
   *     os: ['windows', 'chrome'],
   *     brand: ['acer', 'dell', 'hp'],
   *   },
   * };
   */
  attributes: {},

  /**
   * Get "facet object" [attributes]{@link module:core/view.facet.attributes} for the current location. If attributes are not defined for the current location, they will be gathered and stored before returning, unless the `store` param is set to `false`.
   *
   * @memberOf module:core/view.facet
   *
   * @since 3.0.0
   *
   * @param {boolean} [store] - When `false`, if attributes will not be stored. Useful when the facet object may change (e.g. a destructive initial facet). Default: `true`
   *
   * @return {object}
   *
   * @see [`App.state`]{@link module:core/app.state}
   * @see [`facet.attributes`]{@link module:core/view.facet.attributes}
   * @see Used by:
   * @see [**`facet.filter`**]{@link module:core/view.facet.filter}
   * @see [**`facet.disableUnavailable`**]{@link module:core/view.facet.disableUnavailable}
   *
   * @example
   * {
   *   form_factor: ['notebook', 'desktop'],
   *   os: ['windows', 'chrome'],
   *   brand: ['acer', 'dell', 'hp'],
   * }
   */
  getObject: (store = true) => {
    const location = App.state.current.location;

    // if attributes are defined, just return them
    if (facet.attributes[location]) return facet.attributes[location];

    // otherwise, get attributes
    const facetInputs = container.find('.input[data-facet]');
    const facetObject = inputAttributes(facetInputs, {
      type: App.settings.primary_attribute.type,
      allowMultiple: true,
    }, true);

    // store attributes, unless store param is false
    if (store) facet.attributes[location] = facetObject;

    // return attributes
    return facetObject;
  },

  /**
   * Facet filtering, when enabled on supported [form field]{@link module:core/config._field_type} types, allows only selections to be made that will result in product matches. Works in conjunction with the [`Filter`]{@link module:core/filter} module to determine available view inputs based on product inventory.
   *
   * The facet filtering method can be run either destructively, which removes unavailable inputs from the view, or in the default mode, which disables any unavailable inputs, but leaves them in the view. Destructive faceting is typically done [once during view load]{@link module:core/view~removeUnusedElements}, and the default behavior is used during [input actions]{@link module:core/view.inputActions} or other state changes.
   *
   * An overview of the process:
   *
   * 1. Collect facet inputs, groups, and active inputs for use during the faceting process.
   * 1. [Get filter attributes]{@link module:core/view.inputAttributes} from active facet inputs.
   * 1. [Get facet object]{@link module:core/view.facet.getObject} that contains all possible filter values that could be selected using view facet inputs.
   * 1. Create an initial filter request to verify that current selections produce valid products, [globalizing the request]{@link module:core/view.facet.globalizeRequest} if applicable. [Facet]{@link module:core/filter.facet} to determine remaining filters.
   * 1. If no filter results are found, something may be wrong with the request. Attempt to [fix complex attribute requests]{@link module:core/view.facet.resolveComplexRequests} and facet again. If there are still no results, stop faceting here, as there may be an issue with inventory.
   * 1. If faceting destructively, [remove unavailable attributes]{@link module:core/view.facet.removeUnavailable}. Otherwise, [disable unavailable attributes]{@link module:core/view.facet.disableUnavailable}. **Note that the destructive facet process assumes inputs are in their initial state.**
   * 1. Finally, [update state of any facet-related elements]{@link module:core/view.facet.updateState}.
   *
   * @memberOf module:core/view.facet
   *
   * @since 3.0.0
   *
   * @param {boolean} [destructive] - Forcefully remove unavailable inputs. Typically used on initial load to avoid the presence of inputs that will never be enabled. Default: `false`
   *
   * @see [`inputAttributes`]{@link module:core/view.inputAttributes}
   * @see [`facet.getObject`]{@link module:core/view.facet.getObject}
   * @see [`facet.globalizeRequest`]{@link module:core/view.facet.globalizeRequest}
   * @see [`Filter.facet`]{@link module:core/filter.facet}
   * @see [`facet.resolveComplexRequests`]{@link module:core/view.facet.resolveComplexRequests}
   * @see [`facet.removeUnavailable`]{@link module:core/view.facet.removeUnavailable}
   * @see [`facet.disableUnavailable`]{@link module:core/view.facet.disableUnavailable}
   * @see [`facet.updateState`]{@link module:core/view.facet.updateState}
   * @see Used by:
   * @see [**`inputActions`**]{@link module:core/view.inputActions}
   * @see [**`removeUnusedElements`**]{@link module:core/view~removeUnusedElements}
   *
   */
  filter: (destructive = false) => {
    // if faceting is not enabled in the current view, do nothing
    if (!App.current.settings.facet.enabled) return;
    // get inputs and data
    const facetInputs = container.find('.input[data-facet]');
    const activeInputs = facetInputs.filter('.active');
    const activeAttributes = inputAttributes(activeInputs, App.settings.primary_attribute, true);
    // if faceting destructively, don't save the attributes
    const facetObject = facet.getObject(!destructive);

    // merge in manual filter request, if provided
    Util.merge(activeAttributes, App.current.settings.facet.filter_on);

    // get initial facet to make sure we have products
    let request = {filter_on: activeAttributes};
    // get global facet attribute values, if enabled in current page settings
    request = facet.globalizeRequest(request);
    // get filter options representing the remaining products
    let remainingFilters = Filter.facet(request, facetObject);

    // if we have no products, something is wrong
    if (!Object.keys(remainingFilters).length) {
      // check if there are issues with complex inputs, and get updated request
      request = facet.resolveComplexRequests(request, activeInputs, activeAttributes, facetObject);
      // update remaining filters
      remainingFilters = Filter.facet(request, facetObject);
      // if filters are still not present, there is an issue elsewhere
      if (!Object.keys(remainingFilters).length) {
        console.log("Faceting stopped. There are zero products matching current conditions.");
        return;
      }
    }

    // run facet destructively
    // IMPORTANT: assumes all inputs are in initial state
    if (destructive) facet.removeUnavailable(facetInputs, remainingFilters, request);
    // normal facet
    else facet.disableUnavailable(facetInputs);

    facet.updateState();
  },

  /**
   * Add global attributes to facet filter request, if enabled in [facet settings]{@link module:core/config.settings}. Returns the modified request.
   *
   * @memberOf module:core/view.facet
   *
   * @since 3.1.0
   *
   * @param {object} request - The current view filter request.
   *
   * @return {object}
   *
   * @see Used by:
   * @see [**`facet.filter`**]{@link module:core/view.facet.filter}
   */
  globalizeRequest: (request) => {
    let global = App.current.settings.facet.global;
    if (!global) return request;
    // define array of filter property names that should be added to request
    // uses either the facet global array, or all current filter values
    let properties = Array.isArray(global) ? global : Object.keys(App.state.attr.filter);
    // if value exists for any global attribute, add it to request
    for (let attribute of properties) {
      let value = App.state.attr.filter[attribute];
      if (value) request.filter_on[attribute] = value;
    }

    return request;
  },

  /**
   * Correct issues relating to changing values in inputs with complex attributes, and return updated request once issues are resolved.
   *
   * Changing complex attribute values may create a situation where no facet results are possible. This method deselects any complex attributes values whose dependent values may have changed, and removes those problem attributes from the request.
   *
   * - Identify complex attributes in current request. If none, just return existing filters, as the issue is elsewhere.
   * - For each complex attribute:
   *     - [Facet]{@link module:core/filter.facet} a copy of the request without the current attribute. If product attributes are still not found, this attribute is not the issue; skip to the next attribute.
   *     - If product attributes are found, this attribute was the issue. [Deselect]{@link module:core/form.deselectInputs} the problem input and set the request to our working temp request.
   * - Return request object.
   *
   * @memberOf module:core/view.facet
   *
   * @since 3.1.0
   *
   * @param {object} request - The current filter request.
   * @param {jQuery} activeInputs - Active facet inputs.
   * @param {object} activeAttributes - Attributes from active facet inputs.
   * @param {object} facetObject - Facet object.
   *
   * @return {object}
   *
   * @see [`Filter.facet`]{@link module:core/filter.facet}
   * @see [`facet.availableInputs`]{@link module:core/view.facet.availableInputs}
   * @see [`Form.deselectInputs`]{@link module:core/form.deselectInputs}
   * @see Used by:
   * @see [**`facet.filter`**]{@link module:core/view.facet.filter}
   */
  resolveComplexRequests: (request, activeInputs, activeAttributes, facetObject) => {
    // check request for complex attributes
    let complexAttrs = Object.keys(request.filter_on).filter(attribute => Util.isAttrComplex(attribute));
    // if no complex attributes are present, there is an issue somewhere else, just return request unmodified
    if (!complexAttrs.length) return request;
    // sort complex attributes by length to test deeper attributes first
    complexAttrs.sort((a, b) => b.split(':').length - a.split(':').length);
    // identify problem attributes
    for (let attribute of complexAttrs) {
      // copy request so it can be modified
      let tempRequest = Util.cloneDeep(request);
      // remove attribute and test
      delete tempRequest.filter_on[attribute];
      let results = Filter.facet(tempRequest, facetObject);
      // if still no results, continue
      if (!Object.keys(results).length) continue;
      // if we have results, the current attribute was the issue
      // format attribute
      let problemAttribute = {[attribute]: request.filter_on[attribute]};
      // find the input with this attribute and deselect it
      let problemInput = facet.availableInputs(activeInputs, problemAttribute, request);
      Form.deselectInputs(problemInput, true);
      // update state
      updateState();
      // set request to our working temp request
      request = tempRequest;
      // if we have results, no need to continue
      break;
    }
    // return (possibly) modified request
    return request;
  },

  /**
   * Remove unavailable facet inputs from view.
   *
   * 1. [Available inputs are determined]{@link module:core/view.facet.availableInputs} from filter results, with `destructive` param enabled to keep complex sub-attributes.
   * 1. Inputs to permanently remove are determined from remaining attributes. *Note that along with inputs, containing `li` elements are also removed, to prevent screenreaders from announcing an incorrect number of list items.*
   * 1. An additional pass at determining available inputs is done, but without `destructive` param enabled. Any unavailable inputs from this pass are [disabled]{@link module:core/form.disableInputs} instead of removed.
   *
   * @memberOf module:core/view.facet
   *
   * @since 3.1.0
   *
   * @param {jQuery} facetInputs - Facet inputs in the current view.
   * @param {object} remainingFilters - Remaining facet input attributes.
   * @param {object} request - Current filter request.
   *
   * @see [`facet.availableInputs`]{@link module:core/view.facet.availableInputs}
   * @see [`Form.disableInputs`]{@link module:core/form.disableInputs}
   * @see Used by:
   * @see [**`facet.filter`**]{@link module:core/view.facet.filter}
   */
  removeUnavailable: (facetInputs, remainingFilters, request) => {
    // determine available inputs
    const availableInputs = facet.availableInputs(facetInputs, remainingFilters, request, true);
    // find inputs to remove/disable
    let inputsToRemove = facetInputs.not(availableInputs);
    // a second pass is done to catch any complex inputs that should be disabled until a previous selections is made
    let inputsToDisable = facetInputs.not(facet.availableInputs(availableInputs, remainingFilters, request));
    // remove unavailable inputs
    // note the containing list item is removed to avoid screen readers announcing lists being longer than they actually are
    inputsToRemove.closest('li').remove();
    // disable complex inputs
    Form.disableInputs(inputsToDisable);
  },

  /**
   * Disable unavailable facet inputs.
   *
   * 1. Loop through groups of related facet inputs, e.g. radio inputs, select options, individual checkboxes. Note that inputs are faceted as groups and compared to all selections **outside** of the group in order to allow a user to change their selection within a given input group.
   * 1. For each group, [gather all attributes]{@link module:core/view.inputAttributes} from active (currently selected) inputs outside of the current group, and run [filter faceting]{@link module:core/filter.facet} on group inputs based on these selections.
   * 1. Find [remaining available inputs]{@link module:core/view.facet.availableInputs} based on facet results.
   * 1. [Enable]{@link module:core/form.enableInputs} available inputs, and either visually [disable]{@link module:core/form.disableInputs} or destructively remove unavailable inputs.
   *
   * @memberOf module:core/view.facet
   *
   * @since 3.1.0
   *
   * @param {jQuery} facetInputs - Facet inputs in the current view.
   *
   * @see [`facet.getObject`]{@link module:core/view.facet.getObject}
   * @see [`inputAttributes`]{@link module:core/view.inputAttributes}
   * @see [`facet.availableInputs`]{@link module:core/view.facet.availableInputs}
   * @see [`Form.enableInputs`]{@link module:core/form.enableInputs}
   * @see [`Form.disableInputs`]{@link module:core/form.disableInputs}
   * @see Used by:
   * @see [**`facet.filter`**]{@link module:core/view.facet.filter}
   */
  disableUnavailable: (facetInputs) => {
    // get facet object
    const facetObject = facet.getObject();
    // collect facet groups
    const facetGroups = container.find('.js-facetField');
    // facet each field
    facetGroups.each((i, e) => {
      // get field object
      const field = $(e);
      // initialize request object
      const request = {};
      // gather inputs within group
      const groupInputs = field.find('.input[data-facet]');
      // find active inputs outside of group
      const activeInputs = facetInputs.not(groupInputs).filter('.active');
      // add active input attributes to filter request
      request.filter_on = inputAttributes(activeInputs, App.settings.primary_attribute, true);
      // get filter options representing the remaining products
      const remainingFilters = Filter.facet(request, facetObject);
      // find which inputs are available based on remaining product filters
      const availableInputs = facet.availableInputs(groupInputs, remainingFilters, request, false);
      const unavailableInputs = groupInputs.not(availableInputs);
      // enabled available inputs
      Form.enableInputs(availableInputs);
      // disable unavailable inputs
      Form.disableInputs(unavailableInputs);
    });
  },

  /**
   * Check view inputs against remaining filters, i.e. filter values that still have product results.
   *
   * Any input that contains a property:value pair that is contained in `remainingFilters` is considered "available". Note that if an input contains multiple filter attributes, all of them will need to be available in order for the input to be enabled.
   *
   * @memberOf module:core/view.facet
   *
   * @since 3.0.0
   *
   * @param {jQuery} inputs - jQuery object containing inputs to check against remaining filters.
   * @param {object} remainingFilters - Object containing all product attributes that are still available after filtering.
   * @param {object} request - Current filter request.
   * @param {boolean} [destructive] - Whether facet is running in destructive mode. When enabled, complex attributes which require a previous selection to be made are not considered a failing condition. Default: `false`
   *
   * @return {jQuery}
   *
   * @see Used by:
   * @see [**`facet.resolveComplexRequests`**]{@link module:core/view.facet.resolveComplexRequests}
   * @see [**`facet.removeUnavailable`**]{@link module:core/view.facet.removeUnavailable}
   * @see [**`facet.disableUnavailable`**]{@link module:core/view.facet.disableUnavailable}
   *
   * @example
   * let inputs = $('.input');
   *
   * let remainingFilters = {
   *   processor: ["i3", "i5", "i7"],
   *   ram: [[4, 7.9], [8, 15.9]],
   * }
   *
   * let availableInputs = getAvailableFilterInputs(inputs, remainingFilters);
   *
   * // expected available inputs would be any that contained the following property:value pair:
   * // processor: "i3"
   * // processor: "i5"
   * // processor: "i7"
   * // ram: [4, 7.9]
   * // ram: [8, 15.9]
   */
  availableInputs: (inputs, remainingFilters, request, destructive = false) => {
    // create empty jquery object to add matched elements to
    let availableInputs = $('');

    // loop through each input to check if values are available
    inputs.each((i, element) => {
      // get input filter attributes
      let inputAttributes = Util.parseElementAttributes(element);
      // if filter attributes exist, test if they match the remaining attributes
      if (inputAttributes.length) {
        // determine if current input is available
        // fail if input attributes do not meet certain conditions
        let inputAvailable = inputAttributes.reduce((a, attr) => {
          // if running destructively, attribute is a complex attribute, and a dependent value has not yet been chosen, keep this input for now
          if (destructive && Util.isAttrComplex(attr.name) && !Util.getAttributePaths(attr.name, request.filter_on).length) return true;
          // do product attributes contain values for this attr
          const attrValues = remainingFilters[attr.name];
          // is the input attribute one of the product attribute values
          const valueMatch = attrValues && Util.arrayIncludes(attrValues, attr.value);
          return (!attrValues || !valueMatch) ? false : a
        }, true);

        // add to available inputs
        if (inputAvailable) availableInputs = availableInputs.add($(element));
      }
        // if the input has no filter attributes, consider it available
      // basically, only inputs with filter attributes should be judged
      else availableInputs = availableInputs.add($(element));
    });

    return availableInputs;
  },

  /**
   * Update state of elements related to facet functionality. This is typically run after making changes to the facet inputs that may result in changes to facet elements.
   *
   * - [Show or hide the "remove all filters" button]{@link module:core/view.facet.setRemoveAllState}
   * - [Update info messages]{@link module:core/view.facet.updateInfo} related to faceting or inventory.
   *
   * @memberOf module:core/view.facet
   *
   * @since 3.0.0
   *
   * @see [`facet.setRemoveAllState`]{@link module:core/view.facet.setRemoveAllState}
   * @see [`facet.updateInfo`]{@link module:core/view.facet.updateInfo}
   * @see Used by:
   * @see [**`facet.filter`**]{@link module:core/view.facet.filter}
   * @see [**`facet.reset`**]{@link module:core/view.facet.reset}
   */
  updateState: () => {
    // show/hide clear all button
    facet.setRemoveAllState();
    // update facet info messages
    facet.updateInfo();
  },

  /**
   * Show or hide "clear all filters" button. Button is shown if any facet fields have active selections.
   *
   * @memberOf module:core/view.facet
   *
   * @since 3.0.0
   *
   * @param {jQuery} [activeInputs] - jQuery object containing active facet inputs. If none are found, the button's `active` class is removed. Default: `getActiveInputs(container, '[data-facet]')`
   *
   * @see Used by:
   * @see [**`facet.updateState`**]{@link module:core/view.facet.updateState}
   */
  setRemoveAllState: (activeInputs = getActiveInputs(container, '[data-facet]')) => {
    const button = container.find('.js-clearFilters');
    // enable / disable button
    button.attr('aria-hidden', !activeInputs.length);
  },

  /**
   * Update info messages related to faceting or inventory. Info messages are optional depending on project needs, but can include displaying the lowest price in a set of recommended products, or the number of products remaining.
   *
   * - CSS selectors and actions are defined for each message type. If none of the defined selectors are present, the method stops here. Selectors and their definitions include:
   *     - `.js-price` - [set lowest price]{@link module:core/view.facet.setLowestPrice}
   *     - `.js-products-remaining` - [set products remaining]{@link module:core/view.facet.setProductsRemaining}
   * - Any present selectors have `aria-hidden` set to `true`. The "hidden" state for each selector can be defined with CSS.
   * - [Active facet inputs are gathered]{@link module:core/view.getActiveInputs}, and used to get the current active [input attributes]{@link module:core/view.inputAttributes}. If no facet attributes are currently active, the method stops here.
   * - [Filtered products]{@link module:core/filter.recommend} based on active attributes is retrieved.
   * - For each previously defined selector which exists in the view, the associated action is run. Action methods are supplied the current element (as a jQuery object) and the recommended products array.
   *
   * @memberOf module:core/view.facet
   *
   * @since 3.2.8
   *
   * @see [`facet.setLowestPrice`]{@link module:core/view.facet.setLowestPrice}
   * @see [`facet.setProductsRemaining`]{@link module:core/view.facet.setProductsRemaining}
   * @see [`getActiveInputs`]{@link module:core/view.getActiveInputs}
   * @see [`inputAttributes`]{@link module:core/view.inputAttributes}
   * @see [`Filter.recommend`]{@link module:core/filter.recommend}
   * @see Used by:
   * @see [**`facet.updateState`**]{@link module:core/view.facet.updateState}
   */
  updateInfo: () => {
    // define functions for each associated selector
    let selectorActions = {
      '.js-price': facet.setLowestPrice,
      '.js-products-remaining': facet.setProductsRemaining,
    };
    // gather selectors
    let selectors = Object.keys(selectorActions);
    // do nothing of no message selectors are present
    if (!container.find(selectors.join(', ')).length) return;
    // always hide messages
    const outers = container.find(selectors.map(s => `${s}-outer`).join(', '));
    outers.attr('aria-hidden', true);
    // find active facet attributes
    const facetInputs = getActiveInputs(container, '[data-facet]');
    const activeAttributes = inputAttributes(facetInputs, App.settings.primary_attribute, true);
    // do nothing else if there are no active attributes
    if (!Object.keys(activeAttributes).length) return;
    // get current recommended products
    const products = Filter.recommend({filter_on: activeAttributes, matches_only: true});
    // handle each message type
    for (let [selector, action] of Object.entries(selectorActions)) {
      let el = container.find(selector);
      // if element is not present, skip
      if (!el.length) continue;
      // run action
      action(el, products);
    }
  },

  /**
   * Update the "lowest price" message.
   *
   * If a [lowest price]{@link module:core/filter.lowestPrice} is available in the supplied products, the price will be [formatted]{@link module:core/util.formatNumber}, and the price element updated and shown. If a price is not available, the price element will remain hidden.
   *
   * @memberOf module:core/view.facet
   *
   * @since 3.0.0
   *
   * @param {jQuery} priceEl - jQuery price element.
   * @param {array} products - The current recommended products.
   *
   * @see [`Filter.lowestPrice`]{@link module:core/filter.lowestPrice}
   * @see Used by:
   * @see [**`facet.updateInfo`**]{@link module:core/view.facet.updateInfo}
   */
  setLowestPrice: (priceEl, products) => {
    // get lowest price from products
    const price = Filter.lowestPrice(products);
    // do nothing else if there is no lowest price
    if (!price) return;
    // otherwise update and show lowest price
    priceEl.html(Util.formatNumber(price, App.settings.filter.schema.price.format_number.options));
    container.find('.js-price-outer').attr('aria-hidden', false);
  },


  /**
   * Update the "products remaining" message with the total number of currently recommended products.
   *
   * @memberOf module:core/view.facet
   *
   * @since 3.2.8
   *
   * @param {jQuery} remainingEl - jQuery remaining products element.
   * @param {array} products - The current recommended products.
   *
   * @see Used by:
   * @see [**`facet.updateInfo`**]{@link module:core/view.facet.updateInfo}
   */
  setProductsRemaining: (remainingEl, products) => {
    // update products remaining with product count
    remainingEl.html(products.length);
    container.find('.js-products-remaining-outer').attr({
      'aria-hidden': false,
      'data-product-count': products.length,
    });
  },

  /**
   * Reset facet filter state, e.g. when clearing all filters.
   *
   * - [Inputs are initialized]{@link module:core/form.inputInit}.
   * - [App state is updated]{@link module:core/view.updateState}.
   * - The state of [facet-related elements are updated]{@link module:core/view.facet.updateState}.
   * - Any active "remove filter" buttons are hidden.
   * - [Focus is reset]{@link module:core/view.resetPageFocus} to the beginning of the view.
   * -[ Accordion section active input counts are updated]{@link module:core/view.accordion.updateActiveInputCount} in case accordions are used with facet inputs.
   *
   * @memberOf module:core/view.facet
   *
   * @since 3.0.0
   *
   * @see [`Form.inputInit`]{@link module:core/form.inputInit}
   * @see [`updateState`]{@link module:core/view.updateState}
   * @see [`facet.updateState`]{@link module:core/view.facet.updateState}
   * @see [`resetPageFocus`]{@link module:core/view.resetPageFocus}
   * @see [`accordion.updateActiveInputCount`]{@link module:core/view.accordion.updateActiveInputCount}
   * @see Used by:
   * @see [**`facet.addEventListeners`**]{@link module:core/view.facet.addEventListeners}
   */
  reset: () => {
    Form.inputInit();
    updateState();
    facet.updateState();
    container.find('.js-clearField').attr('aria-hidden', true);
    resetPageFocus();
    facet.filter();
    accordion.updateActiveInputCount(container.find('.js-accordionSection'));
  },
};
